import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FIELD_LIMITS } from '../../utils/inputValidation';
import {
  BookOpen,
  Play,
  CheckCircle,
  Clock,
  Award,
  Download,
  Eye,
  Calendar,
  Target,
  Star,
  AlertCircle,
  XCircle,
  Search,
  Brain,
  Shield,
  Lock,
  PlayCircle,
  X,
  RefreshCw,
  Users,
  ChevronDown,
  ChevronRight,
  Video,
  ArrowLeft,
  ArrowRight,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  SkipForward,
  SkipBack,
  Settings,
  Share2,
  Gauge,
  MapPin
} from 'lucide-react';
import essService from '../../api/services/essService';
import certificateService from '../../api/services/certificateService';
import { formatDate } from '../../utils/stringUtils';

const MyTraining = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    return searchParams.get('tab') || 'courses';
  });

  useEffect(() => {
    const tabParam = searchParams.get('tab') || 'courses';
    if (tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [searchParams, activeTab]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams(prev => {
      const nextParams = new URLSearchParams(prev);
      nextParams.set('tab', tabId);
      return nextParams;
    });
  };
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [detailCourse, setDetailCourse] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCoursePlayer, setShowCoursePlayer] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [completedLessons, setCompletedLessons] = useState(new Set());
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchInputRef = useRef(null);
  const [courseFilterStatus, setCourseFilterStatus] = useState('all');
  const [quizStatusFilter, setQuizStatusFilter] = useState('all');

  // Video player state
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const progressBarRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const controlsTimeoutRef = useRef(null);
  const [hoverTime, setHoverTime] = useState(null);
  const [hoverPosition, setHoverPosition] = useState(0);

  // Real courses data from API
  const [courses, setCourses] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [learningPaths, setLearningPaths] = useState([]);
  const [learningPathsLoading, setLearningPathsLoading] = useState(false);
  const [learningPathsLoaded, setLearningPathsLoaded] = useState(false);
  const [learningPathsError, setLearningPathsError] = useState(null);
  const [showLearningPathModal, setShowLearningPathModal] = useState(false);
  const [selectedLearningPath, setSelectedLearningPath] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  const [downloadingCertificateId, setDownloadingCertificateId] = useState(null);
  const [downloadingDocId, setDownloadingDocId] = useState(null);
  const certificatePreviewRef = useRef(null);
  const initialQuizPlayerState = {
    open: false,
    loading: false,
    assignment: null,
    quiz: null,
    answers: {},
    error: null,
    submitting: false,
    result: null
  };
  const [quizPlayerState, setQuizPlayerState] = useState(initialQuizPlayerState);

  // Sessions pagination state
  const [sessionsCurrentPage, setSessionsCurrentPage] = useState(1);
  const sessionsItemsPerPage = 10;

  // Pagination state for other tabs
  const [coursesCurrentPage, setCoursesCurrentPage] = useState(1);
  const coursesItemsPerPage = 10;
  const [coursesTotalPages, setCoursesTotalPages] = useState(1);
  const [coursesTotal, setCoursesTotal] = useState(0);
  const [coursesLoading, setCoursesLoading] = useState(false);

  const [quizzesCurrentPage, setQuizzesCurrentPage] = useState(1);
  const quizzesItemsPerPage = 10;
  const [quizzesTotalPages, setQuizzesTotalPages] = useState(1);
  const [quizzesTotal, setQuizzesTotal] = useState(0);
  const [quizzesLoading, setQuizzesLoading] = useState(false);
  const [certificatesLoading, setCertificatesLoading] = useState(false);

  const [learningPathsCurrentPage, setLearningPathsCurrentPage] = useState(1);
  const learningPathsItemsPerPage = 10;

  const [sessionsTotalPages, setSessionsTotalPages] = useState(1);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load summary data on mount
  useEffect(() => {
    loadSummary();
  }, []);

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'courses') {
      loadCourses();
    } else if (activeTab === 'quizzes') {
      loadQuizzes();
    } else if (activeTab === 'sessions') {
      loadSessions();
    } else if (activeTab === 'learningPaths' && !learningPathsLoaded) {
      loadLearningPaths();
    }
  }, [activeTab, coursesCurrentPage, quizzesCurrentPage, sessionsCurrentPage, debouncedSearchTerm, courseFilterStatus, quizStatusFilter]);

  // Load courses when page or filters change
  useEffect(() => {
    if (activeTab === 'courses') {
      loadCourses();
    }
  }, [coursesCurrentPage, debouncedSearchTerm, courseFilterStatus]);

  // Load quizzes when page or filters change
  useEffect(() => {
    if (activeTab === 'quizzes') {
      loadQuizzes();
    }
  }, [quizzesCurrentPage, debouncedSearchTerm, quizStatusFilter]);

  // Load sessions when page changes
  useEffect(() => {
    if (activeTab === 'sessions') {
      loadSessions();
    }
  }, [sessionsCurrentPage]);

  // Load certificates when certificates tab is active
  useEffect(() => {
    if (activeTab === 'certificates') {
      loadCertificates();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'learningPaths' && !learningPathsLoaded) {
      loadLearningPaths();
    }
  }, [activeTab, learningPathsLoaded]);

  useEffect(() => {
    const totalPages = Math.max(Math.ceil((learningPaths.length || 0) / learningPathsItemsPerPage), 1);
    if (learningPathsCurrentPage > totalPages) {
      setLearningPathsCurrentPage(totalPages);
    }
  }, [learningPaths.length, learningPathsItemsPerPage, learningPathsCurrentPage]);

  useEffect(() => {
    if (!showCertificateModal || !certificatePreviewRef.current) return;

    const iframe = certificatePreviewRef.current;

    const adjustHeight = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc?.body) {
          const calculatedHeight = doc.body.scrollHeight + 16;
          const maxHeight = window.innerHeight * 0.75;
          iframe.style.height = `${Math.min(calculatedHeight, maxHeight)}px`;
        }
      } catch (error) {
      }
    };

    iframe.addEventListener('load', adjustHeight);
    const timer = setTimeout(adjustHeight, 300);

    return () => {
      iframe.removeEventListener('load', adjustHeight);
      clearTimeout(timer);
    };
  }, [showCertificateModal, selectedCertificate]);

  const loadSummary = async () => {
    try {
      const result = await essService.getTraining({ type: 'summary' });
      if (result.success && result.data) {
        setSummary(result.data.summary || null);
      }
    } catch (err) {
    }
  };

  const loadCourses = async () => {
    setCoursesLoading(true);
    setError(null);
    try {
      const result = await essService.getTraining({
        type: 'courses',
        page: coursesCurrentPage,
        limit: coursesItemsPerPage,
        status: courseFilterStatus !== 'all' ? courseFilterStatus : undefined,
        search: debouncedSearchTerm || undefined
      });
      if (result.success && result.data) {
        // Format courses data - modules now include lessons from backend
        const formattedCourses = (result.data.courses || []).map(course => {
          // Format modules - they now come with lessons from the API
          const formattedModules = (course.modules || []).map((module, index) => {
            // Check if module is completed based on progress
            const moduleProgress = course.progress || 0;
            const totalModules = course.modules?.length || 1;
            const moduleThreshold = ((index + 1) / totalModules) * 100;
            const isCompleted = moduleProgress >= moduleThreshold;

            // Format duration from minutes
            const durationMinutes = module.duration || 0;
            const durationFormatted = durationMinutes > 0
              ? formatDuration(durationMinutes)
              : 'N/A';

            return {
              id: module.id || module.moduleId || index + 1,
              moduleId: module.moduleId,
              title: module.title || `Module ${index + 1}`,
              description: module.description || '',
              duration: durationMinutes,
              durationFormatted: durationFormatted,
              completed: isCompleted,
              isLocked: module.isLocked || false,
              order: module.order || index + 1,
              lessons: module.lessons || [], // Lessons are now included from backend
              prerequisites: module.prerequisites || [],
              learningObjectives: module.learningObjectives || []
            };
          });

          // Calculate total course duration from all lessons in modules
          const calculatedTotalDuration = formattedModules.reduce((total, module) => {
            return total + (module.lessons || []).reduce((sum, lesson) => {
              // Only count duration for video and youtube lessons
              if ((lesson.type === 'video' || lesson.type === 'youtube') && lesson.duration) {
                return sum + (Number(lesson.duration) || 0);
              }
              return sum;
            }, 0);
          }, 0);

          // Use calculated duration from lessons, fallback to course.duration, then course.durationFormatted
          const courseDuration = calculatedTotalDuration > 0
            ? formatDuration(calculatedTotalDuration)
            : (course.duration && Number(course.duration) > 0
              ? formatDuration(Number(course.duration))
              : 'N/A');

          return {
            ...course,
            modules: formattedModules,
            duration: calculatedTotalDuration > 0 ? calculatedTotalDuration : (course.duration || 0),
            durationFormatted: courseDuration,
            quiz: course.quiz || null // Quiz data would come from separate API if needed
          };
        });

        setCourses(formattedCourses);
        if (result.data.pagination?.courses) {
          setCoursesTotalPages(result.data.pagination.courses.totalPages || 1);
          setCoursesTotal(result.data.pagination.courses.total || 0);
        }
      } else {
        setError(result.error || 'Failed to load courses');
        setCourses([]);
      }
    } catch (err) {
      setError('Failed to load courses. Please try again.');
      setCourses([]);
    } finally {
      setCoursesLoading(false);
    }
  };

  const loadQuizzes = async () => {
    setQuizzesLoading(true);
    setError(null);
    try {
      const result = await essService.getTraining({
        type: 'quizzes',
        page: quizzesCurrentPage,
        limit: quizzesItemsPerPage,
        status: quizStatusFilter !== 'all' ? quizStatusFilter : undefined,
        search: debouncedSearchTerm || undefined
      });
      if (result.success && result.data) {
        setQuizzes(result.data.quizzes || []);
        if (result.data.pagination?.quizzes) {
          setQuizzesTotalPages(result.data.pagination.quizzes.totalPages || 1);
          setQuizzesTotal(result.data.pagination.quizzes.total || 0);
        }
      } else {
        setError(result.error || 'Failed to load quizzes');
        setQuizzes([]);
      }
    } catch (err) {
      setError('Failed to load quizzes. Please try again.');
      setQuizzes([]);
    } finally {
      setQuizzesLoading(false);
    }
  };

  const loadSessions = async () => {
    setSessionsLoading(true);
    setError(null);
    try {
      const result = await essService.getTraining({
        type: 'sessions',
        page: sessionsCurrentPage,
        limit: sessionsItemsPerPage
      });
      if (result.success && result.data) {
        setSessions(result.data.sessions || []);
        if (result.data.pagination?.sessions) {
          setSessionsTotalPages(result.data.pagination.sessions.totalPages || 1);
          setSessionsTotal(result.data.pagination.sessions.total || 0);
        }
      } else {
        setError(result.error || 'Failed to load sessions');
        setSessions([]);
      }
    } catch (err) {
      setError('Failed to load sessions. Please try again.');
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  };
  const loadCertificates = async () => {
    setCertificatesLoading(true);
    setError(null);
    try {
      const result = await essService.getTraining({
        type: 'certificates'
      });
      if (result.success && result.data) {
        setCertificates(result.data.certificates || []);
      } else {
        setError(result.error || 'Failed to load certificates');
        setCertificates([]);
      }
    } catch (err) {
      setError('Failed to load certificates. Please try again.');
      setCertificates([]);
    } finally {
      setCertificatesLoading(false);
    }
  };

  const loadLearningPaths = async () => {
    if (learningPathsLoading || learningPathsLoaded) return;
    setLearningPathsLoading(true);
    setLearningPathsError(null);
    try {
      const result = await essService.getLearningPaths();
      if (result.success && result.data) {
        setLearningPaths(result.data.learningPaths || []);
        setLearningPathsLoaded(true);
      } else {
        setLearningPathsError(result.error || 'Failed to load learning paths');
      }
    } catch (error) {
      setLearningPathsError('Failed to load learning paths.');
    } finally {
      setLearningPathsLoading(false);
    }
  };

  const openQuizPlayer = async (quizAssignment) => {
    setQuizPlayerState({
      ...initialQuizPlayerState,
      open: true,
      loading: true,
      assignment: quizAssignment
    });
    try {
      const response = await essService.getQuizAssignment(quizAssignment.id);
      if (response.success) {
        const quizData = response.data;
        // If there's a lastAttempt, show the result/acknowledgment instead of the quiz
        const result = quizData.lastAttempt ? {
          status: quizData.lastAttempt.status,
          score: quizData.lastAttempt.score,
          correctAnswers: quizData.lastAttempt.correctAnswers,
          totalQuestions: quizData.lastAttempt.totalQuestions,
          passed: quizData.lastAttempt.passed,
          answersReview: quizData.lastAttempt.answersReview || [],
          submittedAt: quizData.lastAttempt.submittedAt,
          attemptNumber: quizData.lastAttempt.attemptNumber
        } : null;

        setQuizPlayerState({
          ...initialQuizPlayerState,
          open: true,
          loading: false,
          assignment: quizAssignment,
          quiz: quizData,
          answers: {},
          result: result
        });
      } else {
        setQuizPlayerState(prev => ({
          ...prev,
          loading: false,
          error: response.error || 'Failed to load quiz details'
        }));
      }
    } catch (error) {
      setQuizPlayerState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load quiz details'
      }));
    }
  };

  const closeQuizPlayer = () => {
    setQuizPlayerState(initialQuizPlayerState);
  };

  const handleQuizAnswerChange = (questionId, value) => {
    if (quizPlayerState.result) return;
    setQuizPlayerState(prev => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: value
      },
      error: null
    }));
  };

  const handleSubmitQuizAttempt = async () => {
    if (!quizPlayerState.quiz || quizPlayerState.submitting) return;

    const unanswered = (quizPlayerState.quiz.questions || []).filter((question) => {
      const value = quizPlayerState.answers[question.id];
      if (question.type === 'mcq_single' || question.type === 'true_false') {
        return !value;
      }
      return !value || String(value).trim() === '';
    });

    if (unanswered.length > 0) {
      setQuizPlayerState(prev => ({
        ...prev,
        error: 'Please answer all questions before submitting.'
      }));
      return;
    }

    setQuizPlayerState(prev => ({ ...prev, submitting: true, error: null }));

    try {
      const payload = {
        answers: quizPlayerState.quiz.questions.map((question) => {
          const value = quizPlayerState.answers[question.id];
          if (question.type === 'mcq_single' || question.type === 'true_false') {
            return {
              questionId: question.id,
              selectedOptionId: value || null
            };
          }
          return {
            questionId: question.id,
            answerText: value || ''
          };
        })
      };

      const response = await essService.submitQuizAttempt(quizPlayerState.assignment.id, payload);
      if (response.success) {
        setQuizPlayerState(prev => ({
          ...prev,
          submitting: false,
          result: response.data,
          quiz: prev.quiz
            ? {
              ...prev.quiz,
              attemptsUsed: response.data.attemptsUsed ?? prev.quiz.attemptsUsed
            }
            : prev.quiz
        }));
        // Reload quizzes after submission
        if (activeTab === 'quizzes') {
          loadQuizzes();
        }
      } else {
        setQuizPlayerState(prev => ({
          ...prev,
          submitting: false,
          error: response.error || 'Failed to submit quiz'
        }));
      }
    } catch (error) {
      setQuizPlayerState(prev => ({
        ...prev,
        submitting: false,
        error: 'Failed to submit quiz. Please try again.'
      }));
    }
  };

  // Helper functions for certificate status
  const isCertificateExpired = (certificate) => {
    const expiryDate = certificate.expiryDate || certificate.validUntil;
    if (!expiryDate) return false;
    const today = new Date();
    const expiry = new Date(expiryDate);
    return expiry < today;
  };

  const isCertificateExpiringSoon = (certificate) => {
    const expiryDate = certificate.expiryDate || certificate.validUntil;
    if (!expiryDate) return false;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const daysToExpiry = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return daysToExpiry > 0 && daysToExpiry <= 30; // Expires within 30 days but not yet expired
  };

  const getStatusColor = (status) => {
    // Handle certificate objects
    if (typeof status === 'object' && status !== null) {
      if (isCertificateExpired(status)) return 'text-red-600 bg-red-100';
      if (isCertificateExpiringSoon(status)) return 'text-orange-600 bg-orange-100';
      return 'text-green-600 bg-green-100';
    }

    // Handle string status
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'not_started': return 'text-gray-600 bg-gray-100';
      case 'overdue': return 'text-red-600 bg-red-100';
      case 'valid': return 'text-green-600 bg-green-100';
      case 'expiring_soon': return 'text-yellow-600 bg-yellow-100';
      case 'expired': return 'text-red-600 bg-red-100';
      case 'available': return 'text-blue-600 bg-blue-100';
      case 'locked': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    // Handle certificate objects
    if (typeof status === 'object' && status !== null) {
      if (isCertificateExpired(status)) return <XCircle className="w-4 h-4" />;
      if (isCertificateExpiringSoon(status)) return <AlertCircle className="w-4 h-4" />;
      return <CheckCircle className="w-4 h-4" />;
    }

    // Handle string status
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'in_progress': return <PlayCircle className="w-4 h-4" />;
      case 'not_started': return <Play className="w-4 h-4" />;
      case 'overdue': return <AlertCircle className="w-4 h-4" />;
      case 'valid': return <CheckCircle className="w-4 h-4" />;
      case 'expiring_soon': return <Clock className="w-4 h-4" />;
      case 'expired': return <XCircle className="w-4 h-4" />;
      case 'available': return <Play className="w-4 h-4" />;
      case 'locked': return <Lock className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getQuizDisplayStatus = (quiz) => {
    if (!quiz) return 'assigned';
    if (quiz.lastAttempt?.status === 'passed') {
      return 'passed';
    }
    if (quiz.lastAttempt?.status === 'failed') {
      return 'failed';
    }
    if (quiz.status === 'overdue') {
      return 'overdue';
    }
    return 'assigned';
  };

  const getQuizStatusMeta = (quiz) => {
    const status = getQuizDisplayStatus(quiz);
    switch (status) {
      case 'passed':
        return {
          label: 'Passed',
          color: 'bg-green-100 text-green-700',
          icon: <CheckCircle className="w-4 h-4" />
        };
      case 'failed':
        return {
          label: 'Failed',
          color: 'bg-red-100 text-red-700',
          icon: <XCircle className="w-4 h-4" />
        };
      case 'overdue':
        return {
          label: 'Overdue',
          color: 'bg-red-100 text-red-700',
          icon: <AlertCircle className="w-4 h-4" />
        };
      default:
        return {
          label: 'Assigned',
          color: 'bg-gray-100 text-gray-600',
          icon: <Clock className="w-4 h-4" />
        };
    }
  };

  const formatDisplayValue = (value) => {
    if (value === undefined || value === null) return 'N/A';
    if (typeof value !== 'string') return value;
    const cleaned = value.replace(/_/g, ' ').trim();
    if (!cleaned) {
      return 'N/A';
    }
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
  };

  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    try {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
      const match = url.match(regExp);
      if (match && match[2].length === 11) {
        return match[2];
      }
      if (url.includes('youtube.com/shorts/')) {
        return url.split('youtube.com/shorts/')[1]?.split(/[?#]/)[0];
      }
      if (url.includes('youtube.com/embed/')) {
        return url.split('youtube.com/embed/')[1]?.split(/[?#]/)[0];
      }
      if (url.includes('youtu.be/')) {
        return url.split('youtu.be/')[1]?.split(/[?#]/)[0];
      }
    } catch (e) {
    }
    return null;
  };

  const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) {
      return '0m';
    }
    const totalSeconds = Math.round(minutes * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0) parts.push(`${mins}m`);
    if (secs > 0 && hours === 0) parts.push(`${secs}s`);

    return parts.length > 0 ? parts.join(' ') : '0m';
  };

  // Format time only (without date)
  const formatTimeOnly = (dateTime) => {
    if (!dateTime) return 'N/A';
    try {
      const date = new Date(dateTime);
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      return `${displayHours}:${displayMinutes} ${ampm}`;
    } catch (error) {
      return 'N/A';
    }
  };

  // Truncate text for table display
  const truncateText = (text, maxLength = 40) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatPathDescription = (text) => {
    if (!text || typeof text !== 'string') return 'No description available.';
    const cleaned = text.trim();
    if (!cleaned) return 'No description available.';
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  };

  const renderTabPagination = (currentPage, totalItems, itemsPerPage, onPageChange) => {
    const totalPages = Math.max(Math.ceil(totalItems / itemsPerPage), 1);
    if (totalPages <= 1) return null;

    const safeStartIndex = Math.min((currentPage - 1) * itemsPerPage, Math.max(totalItems - 1, 0));
    const displayStart = totalItems === 0 ? 0 : safeStartIndex + 1;
    const displayEnd = totalItems === 0 ? 0 : Math.min(safeStartIndex + itemsPerPage, totalItems);

    const getPageNumber = (index) => {
      if (totalPages <= 5) {
        return index + 1;
      }
      if (currentPage <= 3) {
        return index + 1;
      }
      if (currentPage >= totalPages - 2) {
        return totalPages - 4 + index;
      }
      return currentPage - 2 + index;
    };

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
          <div className="text-sm text-gray-700">
            Showing {displayStart} to {displayEnd} of {totalItems} results
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                const pageNum = getPageNumber(index);
                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg ${currentPage === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };


  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleStartCourse = async (course) => {
    setLoading(true);
    try {
      // Calculate total course duration from all lessons in modules
      const calculatedTotalDuration = (course.modules || []).reduce((total, module) => {
        return total + (module.lessons || []).reduce((sum, lesson) => {
          // Only count duration for video and youtube lessons
          if ((lesson.type === 'video' || lesson.type === 'youtube') && lesson.duration) {
            return sum + (Number(lesson.duration) || 0);
          }
          return sum;
        }, 0);
      }, 0);

      // Use calculated duration from lessons, fallback to course.duration
      const courseDuration = calculatedTotalDuration > 0
        ? formatDuration(calculatedTotalDuration)
        : (course.duration && Number(course.duration) > 0
          ? formatDuration(Number(course.duration))
          : 'N/A');

      // Ensure course has properly formatted duration
      const courseWithFormattedDuration = {
        ...course,
        duration: calculatedTotalDuration > 0 ? calculatedTotalDuration : (course.duration || 0),
        durationFormatted: courseDuration
      };

      // Set the selected course and open course player
      setSelectedCourse(courseWithFormattedDuration);
      setShowCoursePlayer(true);

      // Update URL search parameters with courseId
      const courseId = course.courseId || course.id || course._id;
      if (courseId) {
        setSearchParams({ courseId: String(courseId) });
      }

      // Initialize completed lessons from course progress data
      const initialCompletedLessons = new Set();
      if (course.modules && Array.isArray(course.modules)) {
        course.modules.forEach(module => {
          if (module.lessons && Array.isArray(module.lessons)) {
            module.lessons.forEach(lesson => {
              // Check if lesson has progress and is completed
              if (lesson.progress && (lesson.progress.status === 'completed' || lesson.progress.status === 'passed')) {
                // Use consistent ID format: prefer lesson.id (MongoDB _id), fallback to lesson.lessonId
                // Convert to string to ensure consistent comparison
                const lessonKey = String(lesson.id || lesson.lessonId || '');
                if (lessonKey && lessonKey !== '') {
                  initialCompletedLessons.add(lessonKey);
                }
              }
            });
          }
        });
      }

      setCompletedLessons(initialCompletedLessons);

      // Find and open the first uncompleted lesson
      let firstUncompletedLesson = null;
      let firstUncompletedModule = null;

      if (course.modules && Array.isArray(course.modules)) {
        for (const module of course.modules) {
          if (module.lessons && Array.isArray(module.lessons)) {
            for (const lesson of module.lessons) {
              // Check if lesson is not completed
              const lessonKey = String(lesson.id || lesson.lessonId || '');
              const isCompleted = lessonKey && initialCompletedLessons.has(lessonKey);

              if (!isCompleted && !lesson.isLocked) {
                firstUncompletedLesson = lesson;
                firstUncompletedModule = module;
                break;
              }
            }
            if (firstUncompletedLesson) break;
          }
        }
      }

      // If found, open the first uncompleted lesson
      if (firstUncompletedLesson && firstUncompletedModule) {
        // Expand the module containing the first uncompleted lesson
        setExpandedModules(new Set([firstUncompletedModule.id || firstUncompletedModule.moduleId]));
        // Use setTimeout to ensure state is set before calling handleStartLesson
        setTimeout(() => {
          handleStartLesson(firstUncompletedLesson, firstUncompletedModule);
        }, 50);
      } else {
        // If all lessons are completed, open the first lesson anyway
        if (course.modules && course.modules.length > 0) {
          const firstModule = course.modules[0];
          if (firstModule.lessons && firstModule.lessons.length > 0) {
            const firstLesson = firstModule.lessons[0];
            setExpandedModules(new Set([firstModule.id || firstModule.moduleId]));
            setTimeout(() => {
              handleStartLesson(firstLesson, firstModule);
            }, 50);
          } else {
            setExpandedModules(new Set([firstModule.id || firstModule.moduleId]));
          }
        }
      }
    } catch (error) {
      alert('Failed to start course');
    } finally {
      setLoading(false);
    }
  };

  // Auto-open course player if courseId is in the URL query parameters
  useEffect(() => {
    const courseIdFromUrl = searchParams.get('courseId');
    if (courseIdFromUrl && courses.length > 0 && !showCoursePlayer) {
      const course = courses.find(c => String(c.courseId || c.id || c._id) === String(courseIdFromUrl));
      if (course) {
        handleStartCourse(course);
      }
    }
  }, [searchParams, courses, showCoursePlayer]);

  const handleViewCourseDetails = (course) => {
    // Re-check ID priority: in essService training response, 'courseId' is the actual course ID,
    // while 'id' is often the assignment ID.
    const courseId = course.courseId || course.id || course._id;
    if (courseId) {
      // Set context for sidebar highlighting
      sessionStorage.setItem('trainingNavContext', 'my_training');
      navigate(`/hrms/training/course/${courseId}`);
    } else {
      setDetailCourse(course);
      setShowDetailModal(true);
    }
  };

  const handleToggleModule = (moduleId) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId);
      } else {
        newSet.add(moduleId);
      }
      return newSet;
    });
  };

  const handleStartLesson = async (lesson, module) => {
    setSelectedLesson(lesson);
    setSelectedModule(module);
    setVideoError(null);
    setIsLoading(true);
    // Reset video player state
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    // Track lesson start time for progress calculation
    const lessonId = String(lesson.id || lesson.lessonId || '');
    if (lessonId && !completedLessons.has(lessonId)) {
      // Mark as started (not completed yet)
      // Progress will be updated when lesson is completed
    }
  };

  // Video player handlers
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (e) => {
    if (!videoRef.current || !progressBarRef.current) return;

    const clientX = typeof e?.clientX === 'number' ? e.clientX : null;
    if (clientX === null) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const pos = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    const newTime = pos * duration;
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
    }
  };

  const handleMuteToggle = () => {
    if (videoRef.current) {
      if (isMuted) {
        videoRef.current.volume = volume || 0.5;
        setIsMuted(false);
      } else {
        videoRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  const handlePlaybackRateChange = (rate) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
      setPlaybackRate(rate);
      setShowSpeedMenu(false);
    }
  };

  const handleFullscreen = () => {
    if (!videoContainerRef.current) return;

    if (!isFullscreen) {
      if (videoContainerRef.current.requestFullscreen) {
        videoContainerRef.current.requestFullscreen();
      } else if (videoContainerRef.current.webkitRequestFullscreen) {
        videoContainerRef.current.webkitRequestFullscreen();
      } else if (videoContainerRef.current.msRequestFullscreen) {
        videoContainerRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  const handleSkip = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, Math.min(duration, currentTime + seconds));
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Close speed menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showSpeedMenu && !e.target.closest('.speed-menu-container')) {
        setShowSpeedMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSpeedMenu]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!showCoursePlayer || !selectedLesson || selectedLesson.type !== 'video') return;

    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON') {
        // Allow typing in inputs and clicking buttons
        if (e.key === 'f' || e.key === 'F' || e.key === ' ') {
          // Still allow fullscreen and play/pause from anywhere
        } else {
          return;
        }
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          handlePlayPause();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (e.shiftKey) {
            handlePreviousLesson();
          } else {
            handleSkip(-10);
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (e.shiftKey) {
            handleNextLesson();
          } else {
            handleSkip(10);
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (videoRef.current) {
            const newVolume = Math.min(1, volume + 0.1);
            videoRef.current.volume = newVolume;
            setVolume(newVolume);
            setIsMuted(false);
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (videoRef.current) {
            const newVolume = Math.max(0, volume - 0.1);
            videoRef.current.volume = newVolume;
            setVolume(newVolume);
            setIsMuted(newVolume === 0);
          }
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          handleFullscreen();
          break;
        case 'm':
        case 'M':
          e.preventDefault();
          handleMuteToggle();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showCoursePlayer, selectedLesson, isPlaying, volume, currentTime, duration]);

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      // Auto-hide controls after 3 seconds of inactivity
      if (showControls) {
        if (controlsTimeoutRef.current) {
          clearTimeout(controlsTimeoutRef.current);
        }
        controlsTimeoutRef.current = setTimeout(() => {
          if (isPlaying) {
            setShowControls(false);
          }
        }, 3000);
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setIsLoading(false);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      // Auto-complete lesson when video ends
      if (selectedLesson && selectedModule) {
        const lessonId = String(selectedLesson.id || selectedLesson.lessonId || '');
        if (lessonId && !completedLessons.has(lessonId)) {
          handleCompleteLesson(selectedLesson, selectedModule);
        }
      }
    };
    const handleError = (e) => {
      setVideoError('Failed to load video. Please try again.');
      setIsLoading(false);
    };
    const handleWaiting = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);

    const handleFullscreenChange = () => {
      setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement));
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('canplay', handleCanPlay);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('canplay', handleCanPlay);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [selectedLesson, showControls, isPlaying]);

  // Reset video when lesson changes
  useEffect(() => {
    if (selectedLesson) {
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setShowControls(true);
      if (videoRef.current) {
        videoRef.current.load();
      }
    }
  }, [selectedLesson?.id || selectedLesson?.lessonId]);

  const handleCompleteLesson = async (lesson, module) => {
    if (!selectedCourse || !selectedCourse.assignmentId) {
      return;
    }

    try {
      // Mark lesson as completed locally
      // Use consistent ID format: prefer lesson.id (MongoDB _id), fallback to lesson.lessonId
      // Convert to string to ensure consistent comparison
      const lessonId = String(lesson.id || lesson.lessonId || '');
      if (!lessonId || lessonId === '') {
        toast.error('Cannot mark lesson as completed: Lesson ID missing');
        return;
      }
      const updatedCompletedLessons = new Set([...completedLessons, lessonId]);
      setCompletedLessons(updatedCompletedLessons);

      // Calculate which modules are now completed
      // A module is completed when ALL its lessons are completed
      const completedModuleIds = [];

      selectedCourse.modules.forEach(m => {
        const moduleLessons = m.lessons || [];
        if (moduleLessons.length === 0) return; // Skip modules with no lessons

        // Check if all lessons in this module are completed
        // Use consistent ID format for comparison
        const allLessonsCompleted = moduleLessons.every(l => {
          const lId = String(l.id || l.lessonId || '');
          return lId && updatedCompletedLessons.has(lId);
        });

        if (allLessonsCompleted) {
          // Module is completed - use the module's MongoDB _id (not moduleId string)
          completedModuleIds.push(m.id); // This is the MongoDB _id from backend
        }
      });

      // Update progress on backend
      // Backend expects lessonId, moduleId, status, timeSpent, score, completed
      const lessonIdToSend = lesson.id || lesson.lessonId; // Use MongoDB _id if available
      const moduleIdToSend = module.id || module.moduleId; // Use MongoDB _id if available

      if (!lessonIdToSend) {
        toast.error('Cannot update progress: Lesson ID missing');
        return;
      }

      const result = await essService.updateTrainingProgress(selectedCourse.assignmentId, {
        lessonId: lessonIdToSend,
        moduleId: moduleIdToSend || null,
        status: 'completed',
        completed: true,
        timeSpent: lesson.duration || 0,
        score: null // Can be set if lesson has a score
      });

      if (result.success) {
        // Reload training data to get updated progress from backend
        const trainingResult = await essService.getTraining();

        if (trainingResult.success && trainingResult.data) {
          // Find the updated course in the fresh data
          const updatedCourse = (trainingResult.data.courses || []).find(c =>
            (c.assignmentId || c.id) === selectedCourse.assignmentId
          );

          if (updatedCourse) {
            // Format the course the same way loadTrainingData does
            const formattedModules = (updatedCourse.modules || []).map((module, index) => {
              const durationMinutes = module.duration || 0;
              const durationFormatted = durationMinutes > 0
                ? formatDuration(durationMinutes)
                : 'N/A';

              return {
                id: module.id || module.moduleId || index + 1,
                moduleId: module.moduleId,
                title: module.title || `Module ${index + 1}`,
                description: module.description || '',
                duration: durationMinutes,
                durationFormatted: durationFormatted,
                isLocked: module.isLocked || false,
                order: module.order || index + 1,
                lessons: module.lessons || [],
                prerequisites: module.prerequisites || [],
                learningObjectives: module.learningObjectives || []
              };
            });

            // Calculate total course duration from all lessons in modules
            const calculatedTotalDuration = formattedModules.reduce((total, module) => {
              return total + (module.lessons || []).reduce((sum, lesson) => {
                // Only count duration for video and youtube lessons
                if ((lesson.type === 'video' || lesson.type === 'youtube') && lesson.duration) {
                  return sum + (Number(lesson.duration) || 0);
                }
                return sum;
              }, 0);
            }, 0);

            // Use calculated duration from lessons, fallback to updatedCourse.duration
            const courseDuration = calculatedTotalDuration > 0
              ? formatDuration(calculatedTotalDuration)
              : (updatedCourse.duration && Number(updatedCourse.duration) > 0
                ? formatDuration(Number(updatedCourse.duration))
                : 'N/A');

            const formattedCourse = {
              ...updatedCourse,
              modules: formattedModules,
              durationFormatted: courseDuration
            };

            // Update selectedCourse with fresh data
            setSelectedCourse(formattedCourse);

            // Re-initialize completedLessons from updated course data
            const updatedCompletedLessons = new Set();
            if (formattedCourse.modules && Array.isArray(formattedCourse.modules)) {
              formattedCourse.modules.forEach(module => {
                if (module.lessons && Array.isArray(module.lessons)) {
                  module.lessons.forEach(lesson => {
                    if (lesson.progress && (lesson.progress.status === 'completed' || lesson.progress.status === 'passed')) {
                      // Use consistent ID format: prefer lesson.id (MongoDB _id), fallback to lesson.lessonId
                      // Convert to string to ensure consistent comparison
                      const lessonKey = String(lesson.id || lesson.lessonId || '');
                      if (lessonKey && lessonKey !== '') {
                        updatedCompletedLessons.add(lessonKey);
                      }
                    }
                  });
                }
              });
            }
            setCompletedLessons(updatedCompletedLessons);
          }
        }

        // Also update the courses list
        if (activeTab === 'courses') {
          await loadCourses();
        }
      } else {
        throw new Error(result.error || 'Failed to update progress');
      }
    } catch (error) {
      // Remove from completed set if update failed
      const lessonId = String(lesson.id || lesson.lessonId || '');
      if (lessonId && lessonId !== '') {
        setCompletedLessons(prev => {
          const newSet = new Set(prev);
          newSet.delete(lessonId);
          return newSet;
        });
      }
      alert('Failed to update progress. Please try again.');
    }
  };

  const handleCloseLesson = () => {
    setSelectedLesson(null);
    setSelectedModule(null);
  };

  const handleNextLesson = () => {
    if (!selectedCourse || !selectedLesson || !selectedModule) return;

    // Find current lesson index
    const currentModuleIndex = selectedCourse.modules.findIndex(
      m => (m.id || m.moduleId) === (selectedModule.id || selectedModule.moduleId)
    );
    const currentLessonIndex = selectedModule.lessons.findIndex(
      l => (l.lessonId || l.id) === (selectedLesson.lessonId || selectedLesson.id)
    );

    // Try next lesson in same module
    if (currentLessonIndex < selectedModule.lessons.length - 1) {
      const nextLesson = selectedModule.lessons[currentLessonIndex + 1];
      if (!nextLesson.isLocked) {
        handleStartLesson(nextLesson, selectedModule);
        return;
      }
    }

    // Try first lesson in next module
    if (currentModuleIndex < selectedCourse.modules.length - 1) {
      const nextModule = selectedCourse.modules[currentModuleIndex + 1];
      if (nextModule.lessons && nextModule.lessons.length > 0) {
        const firstLesson = nextModule.lessons[0];
        if (!firstLesson.isLocked) {
          handleStartLesson(firstLesson, nextModule);
          // Expand the next module
          setExpandedModules(prev => new Set([...prev, nextModule.id || nextModule.moduleId]));
          return;
        }
      }
    }

    // No next lesson available
    alert('You have completed all available lessons!');
  };

  const handlePreviousLesson = () => {
    if (!selectedCourse || !selectedLesson || !selectedModule) return;

    // Find current lesson index
    const currentModuleIndex = selectedCourse.modules.findIndex(
      m => (m.id || m.moduleId) === (selectedModule.id || selectedModule.moduleId)
    );
    const currentLessonIndex = selectedModule.lessons.findIndex(
      l => (l.lessonId || l.id) === (selectedLesson.lessonId || selectedLesson.id)
    );

    // Try previous lesson in same module
    if (currentLessonIndex > 0) {
      const prevLesson = selectedModule.lessons[currentLessonIndex - 1];
      handleStartLesson(prevLesson, selectedModule);
      return;
    }

    // Try last lesson in previous module
    if (currentModuleIndex > 0) {
      const prevModule = selectedCourse.modules[currentModuleIndex - 1];
      if (prevModule.lessons && prevModule.lessons.length > 0) {
        const lastLesson = prevModule.lessons[prevModule.lessons.length - 1];
        handleStartLesson(lastLesson, prevModule);
        // Expand the previous module
        setExpandedModules(prev => new Set([...prev, prevModule.id || prevModule.moduleId]));
        return;
      }
    }

    // No previous lesson
    alert('This is the first lesson');
  };

  // Helper function to check if current lesson is the first lesson
  const isFirstLesson = () => {
    if (!selectedCourse || !selectedLesson || !selectedModule) return false;

    const currentModuleIndex = selectedCourse.modules.findIndex(
      m => (m.id || m.moduleId) === (selectedModule.id || selectedModule.moduleId)
    );
    const currentLessonIndex = selectedModule.lessons.findIndex(
      l => (l.lessonId || l.id) === (selectedLesson.lessonId || selectedLesson.id)
    );

    // Check if it's the first lesson in the first module
    return currentModuleIndex === 0 && currentLessonIndex === 0;
  };

  // Helper function to check if current lesson is the last lesson
  const isLastLesson = () => {
    if (!selectedCourse || !selectedLesson || !selectedModule) return false;

    const currentModuleIndex = selectedCourse.modules.findIndex(
      m => (m.id || m.moduleId) === (selectedModule.id || selectedModule.moduleId)
    );
    const currentLessonIndex = selectedModule.lessons.findIndex(
      l => (l.lessonId || l.id) === (selectedLesson.lessonId || selectedLesson.id)
    );

    // Check if it's the last lesson in the last module
    const lastModuleIndex = selectedCourse.modules.length - 1;
    const lastModule = selectedCourse.modules[lastModuleIndex];
    const lastLessonIndex = lastModule.lessons ? lastModule.lessons.length - 1 : -1;

    return currentModuleIndex === lastModuleIndex && currentLessonIndex === lastLessonIndex;
  };

  const openQuizModal = (quiz) => {
    if (!quiz) return;
    const questionsCount = Array.isArray(quiz.questions)
      ? quiz.questions.length
      : (quiz.questionsCount || quiz.totalQuestions || quiz.questions || null);
    setSelectedQuiz({
      ...quiz,
      questionsCount,
      attempts: quiz.attempts ?? 0,
      maxAttempts: quiz.maxAttempts ?? null,
      passScore: quiz.passScore ?? quiz.passingScore ?? null,
      timeLimit: quiz.timeLimit ?? quiz.duration ?? null
    });
    setShowQuizModal(true);
  };

  const handleStartQuiz = async (quiz) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      openQuizModal(quiz);
    } catch (error) {
      alert('Failed to start quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCertificate = async (certificate) => {
    if (!certificate) return;

    setDownloadingCertificateId(certificate.id);
    try {
      let template = certificate.template || null;

      if (!template && certificate.templateId) {
        try {
          const templateResult = await certificateService.getCertificateTemplate(certificate.templateId);
          if (templateResult.success) {
            template = templateResult.data;
          }
        } catch (error) {
        }
      }

      const certificateHTML = template
        ? generateTemplateBasedHTML(certificate, template)
        : generateDefaultCertificateHTML(certificate);

      const blob = new Blob([certificateHTML], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificate-${certificate.certificateNumber || certificate.certificateId || certificate.id}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Certificate download started');
    } catch (error) {
      toast.error('Failed to download certificate');
    } finally {
      setDownloadingCertificateId(null);
    }
  };

  const generateTemplateBasedHTML = (certificate, template) => {
    if (template?.templateFile && template.templateFile.includes('cloudinary')) {
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Certificate - ${certificate.certificateNumber || certificate.certificateId}</title>
          <style>
            @page { 
              size: A4; 
              margin: 0.5in; 
            }
            body { 
              font-family: 'Times New Roman', serif; 
              margin: 0; 
              padding: 0; 
              background: url('${template.templateFile}') no-repeat center center;
              background-size: contain;
              min-height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .certificate-overlay {
              background: rgba(255, 255, 255, 0.9);
              padding: 40px;
              border-radius: 10px;
              text-align: center;
              max-width: 600px;
              margin: 20px;
            }
            .employee-name {
              font-size: 28px;
              font-weight: bold;
              color: #2d5a27;
              margin: 20px 0;
            }
            .course-name {
              font-size: 20px;
              color: #333;
              margin: 15px 0;
            }
            .details {
              display: flex;
              justify-content: space-around;
              margin: 30px 0;
              flex-wrap: wrap;
            }
            .detail-item {
              text-align: center;
              margin: 10px;
            }
            .detail-label {
              font-size: 14px;
              color: #666;
              margin-bottom: 5px;
              font-weight: bold;
            }
            .detail-value {
              font-size: 18px;
              font-weight: bold;
              color: #2d5a27;
            }
          </style>
        </head>
        <body>
          <div class="certificate-overlay">
            <div class="employee-name">${certificate.employee?.name || certificate.employeeName || 'Learner'}</div>
            <div class="course-name">${certificate.course?.title || certificate.title || 'Course'}</div>
            <div class="details">
              <div class="detail-item">
                <div class="detail-label">Score</div>
                <div class="detail-value">${certificate.score || 0}%</div>
              </div>
              <div class="detail-item">
                <div class="detail-label">Credits</div>
                <div class="detail-value">${certificate.credits || 0}</div>
              </div>
              <div class="detail-item">
                <div class="detail-label">Certificate #</div>
                <div class="detail-value">${certificate.certificateNumber || certificate.certificateId}</div>
              </div>
            </div>
          </div>
        </body>
        </html>
      `;
    }
    return generateDefaultCertificateHTML(certificate);
  };

  const generateDefaultCertificateHTML = (certificate) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Certificate - ${certificate.certificateNumber || certificate.certificateId}</title>
        <style>
          @page { size: A4; margin: 0.5in; }
          body {
            font-family: 'Times New Roman', serif;
            margin: 0;
            padding: 0;
            background: white;
            color: #333;
          }
          .certificate {
            border: 4px solid #2d5a27;
            padding: 60px 40px;
            text-align: center;
            max-width: 800px;
            margin: 0 auto;
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
            position: relative;
          }
          .certificate::before {
            content: '';
            position: absolute;
            top: 20px;
            left: 20px;
            right: 20px;
            bottom: 20px;
            border: 2px solid #2d5a27;
            pointer-events: none;
          }
          .title {
            font-size: 42px;
            font-weight: bold;
            margin-bottom: 15px;
            letter-spacing: 2px;
            color: #2d5a27;
          }
          .subtitle {
            font-size: 20px;
            color: #666;
            font-style: italic;
          }
          .recipient {
            font-size: 32px;
            font-weight: bold;
            color: #2d5a27;
            margin: 30px 0;
          }
          .course {
            font-size: 28px;
            font-weight: bold;
            margin: 30px 0;
          }
          .details {
            margin: 40px 0;
            display: flex;
            justify-content: space-around;
            flex-wrap: wrap;
            gap: 20px;
          }
          .detail-item {
            text-align: center;
            min-width: 120px;
          }
          .detail-label {
            font-size: 16px;
            color: #666;
            margin-bottom: 8px;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .detail-value {
            font-size: 20px;
            font-weight: bold;
            padding: 8px 16px;
            border-radius: 4px;
            display: inline-block;
            min-width: 60px;
          }
          .score { background-color: #d4edda; color: #155724; }
          .credits { background-color: #d1ecf1; color: #0c5460; }
          .cert-num { background-color: #fff3cd; color: #856404; }
          .footer {
            margin-top: 50px;
            font-size: 14px;
            color: #666;
            border-top: 2px solid #2d5a27;
            padding-top: 20px;
          }
          .verification {
            font-weight: bold;
            color: #2d5a27;
          }
        </style>
      </head>
      <body>
        <div class="certificate">
          <div class="title">Certificate of Completion</div>
          <div class="subtitle">This is to certify that</div>
          <div class="recipient">${certificate.employee?.name || certificate.employeeName || 'Learner'}</div>
          <div class="subtitle">has successfully completed the course</div>
          <div class="course">${certificate.course?.title || certificate.title || 'Course'}</div>
          <div class="details">
            <div class="detail-item">
              <div class="detail-label">Score</div>
              <div class="detail-value score">${certificate.score || 0}%</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">Credits</div>
              <div class="detail-value credits">${certificate.credits || 0}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">Certificate #</div>
              <div class="detail-value cert-num">${certificate.certificateNumber || certificate.certificateId}</div>
            </div>
          </div>
          <div class="footer">
            <div>Issued on: ${formatDate(certificate.issuedDate, { includeTime: false })}</div>
            <div>Valid until: ${formatDate(certificate.expiryDate || certificate.validUntil, { includeTime: false })}</div>
            <div class="verification">Verification Code: ${certificate.verificationCode || 'N/A'}</div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const handleViewCertificate = (certificate) => {
    setSelectedCertificate(certificate);
    setShowCertificateModal(true);
  };

  // Filtering is now done on the backend via API

  useEffect(() => {
    setCoursesCurrentPage(1);
  }, [debouncedSearchTerm, courseFilterStatus]);

  useEffect(() => {
    if (coursesCurrentPage > coursesTotalPages && coursesTotalPages > 0) {
      setCoursesCurrentPage(coursesTotalPages);
    }
  }, [coursesTotalPages, coursesCurrentPage]);

  const renderCourses = () => {
    // Use API pagination data - filtering is done on backend
    const totalPages = coursesTotalPages;
    const totalCourses = coursesTotal;

    const handlePageChange = (page) => {
      if (page < 1 || page > totalPages || page === coursesCurrentPage) return;
      setCoursesCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
      <div className="space-y-4">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-end gap-4 w-full">
            {/* Search Bar */}
            <div className="relative flex-[2] min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search courses by title, course type, or instructor..."
                value={String(searchTerm || '')}
                onChange={(e) => {
                  const value = String(e.target.value || '').slice(0, FIELD_LIMITS.search);
                  setSearchTerm(value);
                }}
                maxLength={FIELD_LIMITS.search}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-[42px]"
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    // Maintain focus after clearing
                    setTimeout(() => {
                      if (searchInputRef.current) {
                        searchInputRef.current.focus();
                      }
                    }, 50);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Status Filter */}
            <div className="flex-shrink-0">
              <select
                value={courseFilterStatus}
                onChange={(e) => setCourseFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white h-[42px]"
              >
                <option value="all">All Status</option>
                <option value="not_started">Not Started</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>

            {/* Clear Filters Button */}
            {(searchTerm || courseFilterStatus !== 'all') && (
              <div className="flex-shrink-0">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setCourseFilterStatus('all');
                    // Maintain focus after clearing
                    setTimeout(() => {
                      if (searchInputRef.current) {
                        searchInputRef.current.focus();
                      }
                    }, 50);
                  }}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium h-[42px]"
                  title="Clear all filters"
                >
                  <X className="h-4 w-4" />
                  <span>Clear</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Courses List */}
        <div className="space-y-4">
          {(coursesLoading ? [] : courses).map((course) => {
            const daysUntilDue = getDaysUntilDue(course.dueDate);
            const isOverdue = daysUntilDue !== null && daysUntilDue < 0;
            const isDueSoon = daysUntilDue !== null && daysUntilDue <= 7 && daysUntilDue >= 0;

            return (
              <div
                key={course.id}
                className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-200"
              >
                <div className="p-6 space-y-6">
                  {/* Header */}
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="flex items-start space-x-4 flex-1 min-w-0">
                      <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow text-white">
                        <BookOpen className="w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="text-2xl font-semibold text-gray-900 leading-tight break-words capitalize">
                              {course.title}
                            </h3>
                            <div className="flex flex-wrap items-center gap-2 mt-3">
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                  course.status
                                )}`}
                              >
                                {getStatusIcon(course.status)}
                                <span className="ml-1.5 capitalize">{course.status.replace('_', ' ')}</span>
                              </span>
                            </div>
                          </div>
                          <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-600">
                            <div className="flex items-center mb-1">
                              <Calendar className="w-4 h-4 text-blue-500 mr-2" />
                              <span className="font-semibold text-gray-800">Due Date</span>
                            </div>
                            <p className="text-gray-900 font-medium">
                              {course.dueDate ? formatDate(course.dueDate, { includeTime: false }) : 'No due date'}
                            </p>
                            {daysUntilDue !== null && (
                              <p className={`text-xs font-semibold ${isOverdue ? 'text-red-600' : isDueSoon ? 'text-yellow-600' : 'text-green-600'}`}>
                                {isOverdue
                                  ? `${Math.abs(daysUntilDue)} ${Math.abs(daysUntilDue) === 1 ? 'day' : 'days'} overdue`
                                  : `${daysUntilDue} ${daysUntilDue === 1 ? 'day' : 'days'} left`}
                              </p>
                            )}
                          </div>
                        </div>
                        {course.description && (
                          <div className="mt-3 text-sm text-gray-600 break-words whitespace-pre-line">
                            {course.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Course meta grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    <div className="p-3 rounded-xl border border-gray-100 bg-gray-50">
                      <p className="text-xs uppercase tracking-wide text-gray-500">Course Type</p>
                      <p className="mt-1 text-sm font-semibold text-gray-800 break-words">
                        {formatDisplayValue(course.category)}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl border border-gray-100 bg-gray-50">
                      <p className="text-xs uppercase tracking-wide text-gray-500">Assigned On</p>
                      <p className="mt-1 text-sm font-semibold text-gray-800">
                        {course.assignedDate ? formatDate(course.assignedDate, { includeTime: false }) : 'N/A'}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl border border-gray-100 bg-gray-50">
                      <p className="text-xs uppercase tracking-wide text-gray-500">Due Date</p>
                      <p className="mt-1 text-sm font-semibold text-gray-800">
                        {course.dueDate ? formatDate(course.dueDate, { includeTime: false }) : 'N/A'}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl border border-gray-100 bg-gray-50">
                      <p className="text-xs uppercase tracking-wide text-gray-500">Duration</p>
                      <p className="mt-1 text-sm font-semibold text-gray-800">
                        {course.durationFormatted ||
                          (course.duration && Number(course.duration) > 0
                            ? formatDuration(Number(course.duration))
                            : 'N/A')}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl border border-gray-100 bg-gray-50">
                      <p className="text-xs uppercase tracking-wide text-gray-500">Status</p>
                      <p className="mt-1 text-sm font-semibold text-gray-800">
                        {formatDisplayValue(course.status)}
                      </p>
                    </div>
                  </div>

                  {/* Quiz block */}
                  {course.quiz && (
                    <div className="p-4 border border-blue-100 bg-blue-50 rounded-xl">
                      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800">Assessment</p>
                          <p className="text-sm text-gray-600 capitalize break-words">{course.quiz.title}</p>
                        </div>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(course.quiz.status)}`}>
                          {getStatusIcon(course.quiz.status)}
                          <span className="ml-1 capitalize">{course.quiz.status}</span>
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-gray-600">
                        <div>
                          <p className="uppercase tracking-wide font-semibold text-gray-500">Attempts</p>
                          <p className="mt-1 text-sm font-semibold text-gray-800">
                            {course.quiz.attempts}/{course.quiz.maxAttempts}
                          </p>
                        </div>
                        <div>
                          <p className="uppercase tracking-wide font-semibold text-gray-500">Pass Score</p>
                          <p className="mt-1 text-sm font-semibold text-gray-800">
                            {course.quiz.passScore}%
                          </p>
                        </div>
                        <div>
                          <p className="uppercase tracking-wide font-semibold text-gray-500">Time Limit</p>
                          <p className="mt-1 text-sm font-semibold text-gray-800">
                            {course.quiz.timeLimit} min
                          </p>
                        </div>
                        <div>
                          <p className="uppercase tracking-wide font-semibold text-gray-500">Questions</p>
                          <p className="mt-1 text-sm font-semibold text-gray-800">
                            {course.quiz.questions}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Progress & actions */}
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between text-sm font-semibold text-gray-700 mb-2">
                        <span>Course Progress</span>
                        <span className="text-blue-600">{Math.min(100, Math.max(0, course.progress || 0))}%</span>
                      </div>
                      <div className="h-2.5 w-full bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-2.5 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
                          style={{ width: `${Math.min(100, Math.max(0, course.progress || 0))}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        onClick={() => handleStartCourse(course)}
                        disabled={loading}
                        className="flex items-center space-x-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md transition-all duration-200 font-medium"
                      >
                        <Play className="w-5 h-5" />
                        <span>{course.status === 'not_started' ? 'Start Course' : 'Continue Learning'}</span>
                      </button>
                      {course.quiz && course.quiz.status === 'available' && (
                        <button
                          onClick={() => handleStartQuiz(course.quiz)}
                          disabled={loading}
                          className="flex items-center space-x-2 px-5 py-2.5 text-blue-700 bg-blue-50 border-2 border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium"
                        >
                          <Brain className="w-5 h-5" />
                          <span>Take Quiz</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleViewCourseDetails(course)}
                        className="flex items-center space-x-2 px-5 py-2.5 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium"
                      >
                        <Eye className="w-5 h-5" />
                        <span>View Details</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {renderTabPagination(coursesCurrentPage, totalCourses, coursesItemsPerPage, handlePageChange)}

        {!coursesLoading && courses.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No courses found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || courseFilterStatus !== 'all'
                ? 'Try adjusting your search criteria.'
                : 'No courses available at the moment.'}
            </p>
          </div>
        )}
      </div>
    );
  };

  const getCertificatePreviewHTML = (certificate) => {
    if (!certificate) return '';
    if (certificate.template && certificate.template.templateFile && certificate.template.templateFile.includes('cloudinary')) {
      return generateTemplateBasedHTML(certificate, certificate.template);
    }
    return generateDefaultCertificateHTML(certificate);
  };

  const renderCertificates = () => {
    if (!certificatesLoading && certificates.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <Award className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Certificates</h3>
          <p className="text-sm text-gray-500">You don't have any certificates yet. Complete courses to earn certificates.</p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">My Certificates</h3>
                    </div> */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Course
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Issue Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expiry Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {certificates.map((certificate) => (
                <tr key={certificate.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate" title={certificate.title || certificate.course?.title || 'Unknown Course'}>
                        {certificate.title || certificate.course?.title || 'Unknown Course'}
                      </div>
                      {certificate.courseId && (
                        <div className="text-sm text-gray-500 truncate" title={certificate.courseId}>
                          {certificate.courseId}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {certificate.score || 0}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {certificate.issuedDate ? formatDate(certificate.issuedDate, { includeTime: false }) : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {(certificate.expiryDate || certificate.validUntil)
                      ? formatDate(certificate.expiryDate || certificate.validUntil, { includeTime: false })
                      : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(certificate)}`}>
                      {getStatusIcon(certificate)}
                      <span className="ml-1 capitalize">
                        {isCertificateExpired(certificate) ? 'Expired' :
                          isCertificateExpiringSoon(certificate) ? 'Expiring' :
                            (certificate.status ? certificate.status.replace('_', ' ') : 'Active')}
                      </span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleViewCertificate(certificate)}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View Certificate"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDownloadCertificate(certificate)}
                        disabled={downloadingCertificateId === certificate.id}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Download Certificate"
                      >
                        {downloadingCertificateId === certificate.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };


  const renderQuizModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-900 capitalize break-words flex-1 min-w-0 pr-4">
            {selectedQuiz?.title}
          </h3>
          <button
            onClick={() => setShowQuizModal(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        {selectedQuiz && (
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Quiz Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Questions: {selectedQuiz.questionsCount ?? selectedQuiz.questions ?? 'N/A'}</p>
                  <p className="text-sm text-gray-600">Time Limit: {selectedQuiz.timeLimit ? `${selectedQuiz.timeLimit} minutes` : 'No limit'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Pass Score: {selectedQuiz.passScore != null ? `${selectedQuiz.passScore}%` : 'N/A'}</p>
                  <p className="text-sm text-gray-600">
                    Attempts: {selectedQuiz.attempts ?? 0}
                    {selectedQuiz.maxAttempts != null ? ` / ${selectedQuiz.maxAttempts}` : ' / Unlimited'}
                  </p>
                </div>
              </div>
              {selectedQuiz.dueDate && (
                <p className="text-sm text-gray-600 mt-2">
                  Due by: {formatDate(selectedQuiz.dueDate, { includeTime: false })}
                </p>
              )}
            </div>

            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-yellow-800">Important Instructions</h4>
                  <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                    <li>• {selectedQuiz.timeLimit ? `You have ${selectedQuiz.timeLimit} minutes to complete the quiz` : 'There is no time limit for this quiz'}</li>
                    <li>• You need to score at least {selectedQuiz.passScore != null ? `${selectedQuiz.passScore}%` : 'the passing score'} to pass</li>
                    <li>• {selectedQuiz.maxAttempts != null ? `You have ${Math.max((selectedQuiz.maxAttempts || 0) - (selectedQuiz.attempts || 0), 0)} attempts remaining` : 'Attempts are unlimited'}</li>
                    <li>• Once started, the timer cannot be paused</li>
                  </ul>
                </div>
              </div>
            </div>

            {selectedQuiz.instructions && (
              <div className="p-4 bg-white border border-gray-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Additional Instructions</h4>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedQuiz.instructions}</p>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowQuizModal(false)}
                className="px-4 py-2 text-gray-600 border border-blue-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Start Quiz
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderCoursePlayer = () => {
    if (!selectedCourse) return null;

    return (
      <div className="fixed inset-0 bg-gray-50 z-50 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center">
          <button
            onClick={() => {
              setShowCoursePlayer(false);
              setSelectedLesson(null);
              setSelectedModule(null);
              setSearchParams({});
            }}
            className="p-2 hover:bg-gray-100 rounded-lg mr-4"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 capitalize break-words">{selectedCourse.title}</h2>
            <p className="text-sm text-gray-500">
              Progress: {Math.min(100, Math.max(0, selectedCourse.progress || 0))}% • {
                selectedCourse.durationFormatted ||
                (selectedCourse.duration ? formatDuration(Number(selectedCourse.duration)) : 'N/A')
              }
            </p>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar - Modules and Lessons */}
          <div className="w-72 bg-white border-r border-gray-200 overflow-y-auto">
            <div className="p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Course Content</h3>
              <div className="space-y-1.5">
                {selectedCourse.modules && selectedCourse.modules.map((module) => {
                  const isExpanded = expandedModules.has(module.id || module.moduleId);
                  const moduleLessons = module.lessons || [];
                  const completedLessonsCount = moduleLessons.filter(lesson => {
                    const lId = String(lesson.id || lesson.lessonId || '');
                    return lId && completedLessons.has(lId);
                  }).length;
                  const isModuleCompleted = moduleLessons.length > 0 &&
                    completedLessonsCount === moduleLessons.length;

                  return (
                    <div key={module.id || module.moduleId} className="mb-2">
                      {/* Module Header */}
                      <button
                        onClick={() => handleToggleModule(module.id || module.moduleId)}
                        className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-gray-50 rounded-md transition-colors group"
                      >
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          )}
                          {isModuleCompleted ? (
                            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                          ) : (
                            <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
                          )}
                          <div className="flex-1 text-left min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate capitalize">{module.title}</p>
                            <p className="text-xs text-gray-500">
                              {completedLessonsCount}/{moduleLessons.length} lessons
                            </p>
                          </div>
                        </div>
                      </button>

                      {/* Lessons List */}
                      {isExpanded && moduleLessons.length > 0 && (
                        <div className="ml-7 mt-1 space-y-0.5">
                          {moduleLessons.map((lesson, lessonIndex) => {
                            // Use consistent ID format: prefer lesson.id (MongoDB _id), fallback to lesson.lessonId
                            // Convert to string to ensure consistent comparison
                            const lessonId = String(lesson.id || lesson.lessonId || '');
                            const isCompleted = lessonId && completedLessons.has(lessonId);
                            const isLocked = lesson.isLocked || false;

                            return (
                              <button
                                key={lessonId}
                                onClick={() => !isLocked && handleStartLesson(lesson, module)}
                                disabled={isLocked}
                                className={`w-full px-3 py-2 flex items-center space-x-2 hover:bg-gray-50 rounded-md transition-colors text-left group ${selectedLesson && String(selectedLesson.id || selectedLesson.lessonId || '') === lessonId
                                  ? 'bg-blue-50 text-blue-700'
                                  : 'text-gray-700'
                                  } ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                {isCompleted ? (
                                  <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                                ) : (
                                  <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm truncate capitalize ${selectedLesson && String(selectedLesson.id || selectedLesson.lessonId || '') === lessonId ? 'font-semibold' : 'font-normal'}`}>
                                    {lesson.title}
                                  </p>
                                  <div className="flex items-center space-x-2 mt-0.5">
                                    {(lesson.type === 'video' || lesson.type === 'youtube') && <Video className="w-3 h-3 text-blue-500" />}
                                    {lesson.type === 'quiz' && <Brain className="w-3 h-3 text-purple-500" />}
                                    {(lesson.type === 'text' || lesson.type === 'document' || lesson.type === 'file') && <BookOpen className="w-3 h-3 text-gray-400" />}
                                    {Number(lesson.duration) > 0 && (
                                      <span className="text-xs text-gray-500">
                                        {formatDuration(Number(lesson.duration))}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {isLocked && <Lock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Content Area - Lesson Viewer */}
          <div className="flex-1 overflow-y-auto bg-gray-50">
            {selectedLesson ? (
              <div className="w-full px-8 py-6">
                {/* Lesson Header */}
                <div className="mb-5 mx-auto">
                  <div className="flex items-center space-x-2 mb-2">
                    {(selectedLesson.type === 'video' || selectedLesson.type === 'youtube') && <Video className="w-5 h-5 text-blue-500" />}
                    {selectedLesson.type === 'quiz' && <Brain className="w-5 h-5 text-purple-500" />}
                    {(selectedLesson.type === 'text' || selectedLesson.type === 'document' || selectedLesson.type === 'file') && <BookOpen className="w-5 h-5 text-gray-500" />}
                    <h3 className="text-xl font-semibold text-gray-900 capitalize">{selectedLesson.title}</h3>
                  </div>
                  {selectedLesson.description &&
                    selectedLesson.description.trim() !== '' &&
                    selectedLesson.description.trim() !== '0' &&
                    selectedLesson.description.trim() !== '0Text' &&
                    !selectedLesson.description.trim().startsWith('0Text') &&
                    selectedLesson.description.trim() !== 'Text' && (
                      <p className="text-gray-600 text-sm mb-2 break-words whitespace-pre-line">{selectedLesson.description}</p>
                    )}
                  {selectedLesson.duration && Number(selectedLesson.duration) > 0 && (
                    <div className="flex items-center space-x-1 text-sm text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span>{formatDuration(Number(selectedLesson.duration))} {formatDisplayValue(selectedLesson.type)}</span>
                    </div>
                  )}
                </div>

                {/* Lesson Content */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden mx-auto">
                  {(selectedLesson.type === 'video' || selectedLesson.type === 'youtube') && (
                    <div>
                      {(() => {
                        const isUrl = (str) => {
                          if (typeof str !== 'string') return false;
                          const trimmed = str.trim();
                          return trimmed.startsWith('http://') || 
                                 trimmed.startsWith('https://') || 
                                 trimmed.startsWith('/') || 
                                 trimmed.startsWith('./');
                        };

                        // Determine video source
                        let videoSrc = null;

                        // 1. Check files array for youtube type files or video files (highest priority)
                        if (selectedLesson.files && selectedLesson.files.length > 0) {
                          const ytFile = selectedLesson.files.find(file => 
                            file.type === 'youtube' || 
                            (file.url && (file.url.includes('youtube.com') || file.url.includes('youtu.be')))
                          );
                          if (ytFile) {
                            videoSrc = ytFile.url;
                          } else {
                            const videoFile = selectedLesson.files.find(file => {
                              const fileName = file.name?.toLowerCase() || '';
                              const fileType = file.type?.toLowerCase() || '';
                              return fileName.endsWith('.mp4') ||
                                fileName.endsWith('.webm') ||
                                fileName.endsWith('.ogg') ||
                                fileType.includes('video/mp4') ||
                                fileType.includes('video/webm') ||
                                fileType.includes('video/ogg') ||
                                fileType.includes('video/');
                            });
                            videoSrc = videoFile?.url;
                          }
                        }

                        // 2. Check videoUrl if it's a valid URL
                        if (!videoSrc && isUrl(selectedLesson.videoUrl)) {
                          videoSrc = selectedLesson.videoUrl;
                        }

                        // 3. Check content if it's a valid URL
                        if (!videoSrc && isUrl(selectedLesson.content)) {
                          videoSrc = selectedLesson.content;
                        }

                        // 4. Check externalLinks
                        if (!videoSrc && selectedLesson.externalLinks && selectedLesson.externalLinks.length > 0) {
                          const ytLink = selectedLesson.externalLinks.find(link => 
                            link.url && (link.url.includes('youtube.com') || link.url.includes('youtu.be'))
                          );
                          videoSrc = ytLink?.url || selectedLesson.externalLinks[0]?.url;
                        }

                        // 5. Fallback to first file url if still no videoSrc
                        if (!videoSrc && selectedLesson.files && selectedLesson.files.length > 0) {
                          videoSrc = selectedLesson.files[0]?.url;
                        }

                        const isYoutube = selectedLesson.type === 'youtube' || 
                          (videoSrc && (videoSrc.includes('youtube.com') || videoSrc.includes('youtu.be')));

                        if (videoSrc && isYoutube) {
                          const youtubeId = getYoutubeVideoId(videoSrc);
                          return (
                            <div className="w-full">
                              <div className="relative aspect-video bg-black overflow-hidden rounded-t-lg">
                                {youtubeId ? (
                                  <iframe
                                    title={selectedLesson.title}
                                    className="w-full h-full border-0"
                                    src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                  />
                                ) : (
                                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-75">
                                    <div className="text-center text-white p-6">
                                      <AlertCircle className="w-12 h-12 mx-auto mb-4" />
                                      <p className="text-lg font-semibold mb-2">Invalid YouTube URL</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        }

                        return videoSrc ? (
                          <div className="w-full">
                            <div
                              ref={videoContainerRef}
                              className="relative aspect-video bg-black overflow-hidden group"
                              onMouseMove={() => {
                                setShowControls(true);
                                if (controlsTimeoutRef.current) {
                                  clearTimeout(controlsTimeoutRef.current);
                                }
                              }}
                              onMouseLeave={() => {
                                if (isPlaying) {
                                  controlsTimeoutRef.current = setTimeout(() => {
                                    setShowControls(false);
                                  }, 2000);
                                }
                              }}
                            >
                              {/* Video Element */}
                              <video
                                ref={videoRef}
                                key={videoSrc}
                                src={videoSrc}
                                preload="metadata"
                                className="w-full h-full"
                                onClick={handlePlayPause}
                              >
                                Your browser does not support the video tag.
                              </video>

                              {/* Loading Spinner */}
                              {isLoading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                                </div>
                              )}

                              {/* Error Message */}
                              {videoError && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-75">
                                  <div className="text-center text-white p-6">
                                    <AlertCircle className="w-12 h-12 mx-auto mb-4" />
                                    <p className="text-lg font-semibold mb-2">{videoError}</p>
                                    <button
                                      onClick={() => {
                                        setVideoError(null);
                                        if (videoRef.current) {
                                          videoRef.current.load();
                                        }
                                      }}
                                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                    >
                                      Retry
                                    </button>
                                  </div>
                                </div>
                              )}

                              {/* Custom Controls Overlay */}
                              <div
                                className={`absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'
                                  }`}
                              >
                                {/* Center Play Button */}
                                {!isPlaying && !isLoading && !videoError && (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <button
                                      onClick={handlePlayPause}
                                      className="w-16 h-16 bg-blue-600/90 text-white rounded-full flex items-center justify-center hover:bg-blue-700 hover:scale-110 transition-all duration-300 shadow-lg"
                                    >
                                      <Play className="w-8 h-8 ml-1 fill-white" />
                                    </button>
                                  </div>
                                )}

                                {/* Bottom Controls Bar */}
                                <div className="absolute bottom-0 left-0 right-0 p-4 space-y-3">
                                  {/* Progress Bar Container */}
                                  <div className="flex items-center space-x-3 group/progress">
                                    <span className="text-xs text-white font-mono select-none">
                                      {formatDuration(currentTime / 60)}
                                    </span>
                                    <div
                                      ref={progressBarRef}
                                      onClick={handleSeek}
                                      className="flex-1 h-1.5 bg-white/30 rounded-full cursor-pointer relative group-hover/progress:h-2 transition-all overflow-hidden"
                                    >
                                      <div
                                        className="h-full bg-blue-500 rounded-full relative"
                                        style={{ width: `${(currentTime / (duration || 1)) * 100}%` }}
                                      />
                                    </div>
                                    <span className="text-xs text-white font-mono select-none">
                                      {formatDuration(duration / 60)}
                                    </span>
                                  </div>

                                  {/* Actions Bar */}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                      {/* Play/Pause */}
                                      <button
                                        onClick={handlePlayPause}
                                        className="text-white hover:text-blue-400 transition-colors"
                                      >
                                        {isPlaying ? (
                                          <Pause className="w-5 h-5" />
                                        ) : (
                                          <Play className="w-5 h-5 fill-white" />
                                        )}
                                      </button>

                                      {/* Skip Backward */}
                                      <button
                                        onClick={() => handleSkip(-10)}
                                        className="text-white hover:text-blue-400 transition-colors"
                                      >
                                        <SkipBack className="w-5 h-5" />
                                      </button>

                                      {/* Skip Forward */}
                                      <button
                                        onClick={() => handleSkip(10)}
                                        className="text-white hover:text-blue-400 transition-colors"
                                      >
                                        <SkipForward className="w-5 h-5" />
                                      </button>

                                      {/* Volume Control */}
                                      <div className="flex items-center space-x-2 group/volume">
                                        <button
                                          onClick={handleMuteToggle}
                                          className="text-white hover:text-blue-400 transition-colors"
                                        >
                                          {isMuted || volume === 0 ? (
                                            <VolumeX className="w-5 h-5" />
                                          ) : (
                                            <Volume2 className="w-5 h-5" />
                                          )}
                                        </button>
                                        <input
                                          type="range"
                                          min="0"
                                          max="1"
                                          step="0.05"
                                          value={isMuted ? 0 : volume}
                                          onChange={handleVolumeChange}
                                          className="w-0 group-hover/volume:w-20 accent-blue-500 h-1 bg-white/30 rounded-lg appearance-none cursor-pointer transition-all duration-300 opacity-0 group-hover/volume:opacity-100"
                                        />
                                      </div>
                                    </div>

                                    <div className="flex items-center space-x-4">
                                      {/* Speed Control */}
                                      <div className="relative speed-menu-container">
                                        <button
                                          onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                                          className="flex items-center space-x-1 text-white hover:text-blue-400 transition-colors text-sm font-semibold"
                                        >
                                          <Settings className="w-4 h-4" />
                                          <span>{playbackRate}x</span>
                                        </button>

                                        {showSpeedMenu && (
                                          <div className="absolute bottom-full right-0 mb-2 bg-gray-900 bg-opacity-95 rounded-lg shadow-xl py-2 min-w-[120px] z-50 border border-gray-700">
                                            {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((rate) => (
                                              <button
                                                key={rate}
                                                onClick={() => handlePlaybackRateChange(rate)}
                                                className={`w-full px-4 py-2 text-left text-white hover:bg-blue-600 transition-colors ${playbackRate === rate ? 'bg-blue-600 font-semibold' : ''
                                                  }`}
                                              >
                                                {rate}x {rate === 1 ? '(Normal)' : ''}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-12">
                            <Video className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600">No video available for this lesson</p>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {(selectedLesson.type === 'document' || selectedLesson.type === 'file') && (
                    <div className="p-12 text-center flex flex-col items-center justify-center">
                      <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-4 text-blue-600">
                        <BookOpen className="w-10 h-10" />
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">{selectedLesson.title}</h4>
                      {selectedLesson.description && (
                        <p className="text-gray-500 max-w-md mb-6">{selectedLesson.description}</p>
                      )}
                      {(() => {
                        const isUrl = (str) => {
                          if (typeof str !== 'string') return false;
                          const trimmed = str.trim();
                          return trimmed.startsWith('http://') || 
                                 trimmed.startsWith('https://') || 
                                 trimmed.startsWith('/') || 
                                 trimmed.startsWith('./');
                        };

                        let docUrl = null;
                        let docName = selectedLesson.title;
                        
                        if (selectedLesson.files && selectedLesson.files.length > 0) {
                          const docFile = selectedLesson.files[0];
                          docUrl = docFile.url;
                          docName = docFile.name || docName;
                        }
                        
                        if (!docUrl && isUrl(selectedLesson.videoUrl)) {
                          docUrl = selectedLesson.videoUrl;
                        }
                        
                        if (!docUrl && isUrl(selectedLesson.content)) {
                          docUrl = selectedLesson.content;
                        }
                        
                        const handleDownloadClick = async () => {
                          const currentLessonId = selectedLesson.id || selectedLesson.lessonId;
                          if (downloadingDocId) return;
                          setDownloadingDocId(currentLessonId);
                          try {
                            const response = await fetch(docUrl);
                            const blob = await response.blob();
                            const blobUrl = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = blobUrl;
                            link.download = docName;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(blobUrl);
                          } catch (error) {
                            window.open(docUrl, '_blank', 'noopener,noreferrer');
                          } finally {
                            setDownloadingDocId(null);
                          }
                        };

                        const isDownloading = downloadingDocId === (selectedLesson.id || selectedLesson.lessonId);

                        return docUrl ? (
                          <button
                            onClick={handleDownloadClick}
                            disabled={isDownloading}
                            className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-all font-medium shadow-sm hover:shadow-md"
                          >
                            {isDownloading ? (
                              <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                <span>Downloading...</span>
                              </>
                            ) : (
                              <>
                                <Download className="w-5 h-5" />
                                <span>Download / View Document</span>
                              </>
                            )}
                          </button>
                        ) : (
                          <p className="text-gray-400 italic">No document file available for this lesson</p>
                        );
                      })()}
                    </div>
                  )}

                  {selectedLesson.type === 'text' && (
                    <div className="p-6">
                      {selectedLesson.content &&
                        String(selectedLesson.content).trim() !== '' &&
                        String(selectedLesson.content).trim() !== '0' &&
                        String(selectedLesson.content).trim() !== '0Text' &&
                        !String(selectedLesson.content).trim().startsWith('0Text') &&
                        String(selectedLesson.content).trim() !== 'Text' &&
                        !String(selectedLesson.content).trim().match(/^0+$/) && (
                          <div className="prose max-w-none">
                            <div className="whitespace-pre-wrap text-gray-700">{selectedLesson.content}</div>
                          </div>
                        )}
                    </div>
                  )}

                  {selectedLesson.type === 'quiz' && (
                    <div className="p-6 text-center">
                      <Brain className="w-16 h-16 text-purple-500 mx-auto mb-4" />
                      <p className="text-gray-600 mb-4">Quiz functionality coming soon</p>
                      <p className="text-sm text-gray-500">
                        {selectedLesson.content &&
                          String(selectedLesson.content).trim() !== '' &&
                          String(selectedLesson.content).trim() !== '0' &&
                          String(selectedLesson.content).trim() !== '0Text' &&
                          !String(selectedLesson.content).trim().startsWith('0Text') &&
                          String(selectedLesson.content).trim() !== 'Text' &&
                          !String(selectedLesson.content).trim().match(/^0+$/)
                          ? selectedLesson.content
                          : 'No quiz content available'}
                      </p>
                    </div>
                  )}

                  {/* Lesson Actions */}
                  <div className="p-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      {/* Previous button - only show if not first lesson */}
                      {!isFirstLesson() ? (
                        <button
                          onClick={handlePreviousLesson}
                          className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-all text-sm font-medium"
                          title="Previous Lesson (Shift + ←)"
                        >
                          <ArrowLeft className="w-4 h-4" />
                          <span>Previous</span>
                        </button>
                      ) : (
                        <div></div>
                      )}

                      <div className="flex items-center space-x-3">
                        {!completedLessons.has(String(selectedLesson.id || selectedLesson.lessonId || '')) ? (
                          <button
                            onClick={() => handleCompleteLesson(selectedLesson, selectedModule)}
                            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium transition-all"
                          >
                            <CheckCircle className="w-4 h-4" />
                            <span>Mark Complete</span>
                          </button>
                        ) : (
                          <div className="flex items-center space-x-2 px-4 py-2 bg-green-50 border border-green-200 rounded-md">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-green-700">Completed</span>
                          </div>
                        )}

                        {/* Next button - only show if not last lesson */}
                        {!isLastLesson() && (
                          <button
                            onClick={handleNextLesson}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition-all"
                            title="Next Lesson (Shift + →)"
                          >
                            <span>Next</span>
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full min-h-[400px]">
                <div className="text-center max-w-md px-6">
                  <div className="mb-6">
                    <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Play className="w-12 h-12 text-white" fill="currentColor" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2">Ready to Learn?</h3>
                  <p className="text-gray-600 mb-6">
                    Select a lesson from the sidebar to begin your learning journey. Complete lessons to track your progress and unlock new content.
                  </p>
                  {selectedCourse && selectedCourse.modules && selectedCourse.modules.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800">
                        <strong>{selectedCourse.modules.reduce((total, m) => total + (m.lessons?.length || 0), 0)}</strong> lessons available across <strong>{selectedCourse.modules.length}</strong> modules
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCourseDetailModal = () => {
    if (!detailCourse) return null;

    const infoFields = [
      detailCourse.category && { label: 'Course Type', value: formatDisplayValue(detailCourse.category) },
      detailCourse.type && { label: 'Course Type', value: formatDisplayValue(detailCourse.type) },
      detailCourse.assignedDate && {
        label: 'Assigned On',
        value: formatDate(detailCourse.assignedDate, { includeTime: false })
      },
      detailCourse.dueDate && {
        label: 'Due Date',
        value: formatDate(detailCourse.dueDate, { includeTime: false })
      },
      (detailCourse.durationFormatted || (detailCourse.duration && Number(detailCourse.duration) > 0)) &&
      {
        label: 'Duration',
        value: detailCourse.durationFormatted ||
          (detailCourse.duration && Number(detailCourse.duration) > 0
            ? formatDuration(Number(detailCourse.duration))
            : 'N/A')
      },
      detailCourse.status && { label: 'Status', value: formatDisplayValue(detailCourse.status.replace('_', ' ')) }
    ].filter(Boolean);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 px-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="border-b border-gray-200 px-6 py-4 flex items-start justify-between">
            <div className="pr-6">
              <h3 className="text-2xl font-semibold text-gray-900 break-words capitalize">
                {detailCourse.title}
              </h3>
              {detailCourse.status && (
                <p className="text-sm text-gray-500 mt-1">
                  {formatDisplayValue(detailCourse.status)}
                </p>
              )}
            </div>
            <button
              onClick={() => {
                setShowDetailModal(false);
                setDetailCourse(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-6">
            {infoFields.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {infoFields.map((field, idx) => (
                  <div key={`${field.label}-${idx}`} className="p-4 rounded-xl border border-gray-100 bg-gray-50">
                    <p className="text-xs uppercase tracking-wide text-gray-500">{field.label}</p>
                    <p className="text-sm font-semibold text-gray-800 mt-1 break-words">
                      {field.value}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {detailCourse.description && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Description</h4>
                <p className="text-sm text-gray-700 whitespace-pre-line break-words">
                  {detailCourse.description}
                </p>
              </div>
            )}

            {detailCourse.modules && detailCourse.modules.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Modules</h4>
                <div className="space-y-2">
                  {detailCourse.modules.map((module, idx) => (
                    <div key={module.id || idx} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 break-words capitalize">{module.title}</p>
                          <p className="text-xs text-gray-500">
                            Lessons: {module.lessons?.length || 0}
                            {module.durationFormatted && ` • ${module.durationFormatted}`}
                          </p>
                        </div>
                        {module.isLocked && (
                          <span className="text-xs text-red-500 font-semibold">Locked</span>
                        )}
                      </div>
                      {module.description && (
                        <p className="text-xs text-gray-600 mt-2 break-words">
                          {module.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCertificateModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Certificate Preview</h3>
            {selectedCertificate && (
              <p className="text-sm text-gray-500 mt-1">
                {selectedCertificate.title} • {selectedCertificate.courseId}
              </p>
            )}
          </div>
          <button
            onClick={() => setShowCertificateModal(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {selectedCertificate && (
          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
              <iframe
                ref={certificatePreviewRef}
                title="Certificate Preview"
                srcDoc={getCertificatePreviewHTML(selectedCertificate)}
                className="w-full"
                style={{ minHeight: '400px', border: 'none' }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderSessionModal = () => {
    if (!selectedSession) return null;

    const sessionDate = selectedSession.startTime ? new Date(selectedSession.startTime) : null;
    const sessionDateStr = sessionDate ? sessionDate.toISOString().split('T')[0] : null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 px-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="border-b border-gray-200 px-6 py-4 flex items-start justify-between">
            <div className="pr-6 flex-1">
              <h3 className="text-2xl font-semibold text-gray-900 break-words capitalize">
                {selectedSession.title || 'Untitled Session'}
              </h3>
              {selectedSession.courseTitle && (
                <p className="text-sm text-gray-500 mt-1 break-words">
                  Course: {selectedSession.courseTitle}
                </p>
              )}
            </div>
            <button
              onClick={() => {
                setShowSessionModal(false);
                setSelectedSession(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-6">
            {/* Session Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl border border-gray-100 bg-gray-50">
                <p className="text-xs uppercase tracking-wide text-gray-500">Session Type</p>
                <div className="mt-2 flex items-center">
                  {selectedSession.type === 'classroom' || selectedSession.type === 'in-person' ? (
                    <>
                      <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        In-Person
                      </span>
                    </>
                  ) : (
                    <>
                      <Video className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                        Virtual
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="p-4 rounded-xl border border-gray-100 bg-gray-50">
                <p className="text-xs uppercase tracking-wide text-gray-500">Your Role</p>
                <p className="text-sm font-semibold text-gray-800 mt-1">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${selectedSession.role === 'trainer' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                    {selectedSession.role === 'trainer' ? 'Trainer' : 'Participant'}
                  </span>
                </p>
              </div>

              {sessionDateStr && (
                <div className="p-4 rounded-xl border border-gray-100 bg-gray-50">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Date</p>
                  <p className="text-sm font-semibold text-gray-800 mt-1 flex items-center">
                    <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                    {formatDate(sessionDateStr, { includeTime: false })}
                  </p>
                </div>
              )}

              {selectedSession.startTime && selectedSession.endTime && (
                <div className="p-4 rounded-xl border border-gray-100 bg-gray-50">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Time</p>
                  <p className="text-sm font-semibold text-gray-800 mt-1 flex items-center">
                    <Clock className="h-4 w-4 mr-1 text-gray-400" />
                    {formatTimeOnly(selectedSession.startTime)} - {formatTimeOnly(selectedSession.endTime)}
                  </p>
                </div>
              )}

              {selectedSession.duration && (
                <div className="p-4 rounded-xl border border-gray-100 bg-gray-50">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Duration</p>
                  <p className="mt-1 text-sm font-semibold text-gray-800">
                    {formatDuration(selectedSession.duration)}
                  </p>
                </div>
              )}

              {selectedSession.location && (
                <div className="p-4 rounded-xl border border-gray-100 bg-gray-50">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Location</p>
                  <p className="text-sm font-semibold text-gray-800 mt-1 flex items-center">
                    <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                    {selectedSession.location}
                  </p>
                </div>
              )}
            </div>

            {/* Attendance Status */}
            {selectedSession.attendance && (
              <div className="p-4 rounded-xl border border-gray-100 bg-gray-50">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Attendance Status</p>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedSession.attendance.status === 'present' ? 'bg-green-100 text-green-800' :
                  selectedSession.attendance.status === 'absent' ? 'bg-red-100 text-red-800' :
                    selectedSession.attendance.status === 'late' ? 'bg-yellow-100 text-yellow-800' :
                      selectedSession.attendance.status === 'excused' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                  }`}>
                  {selectedSession.attendance.status === 'present' && <CheckCircle className="w-4 h-4 mr-1" />}
                  {selectedSession.attendance.status === 'absent' && <XCircle className="w-4 h-4 mr-1" />}
                  {selectedSession.attendance.status === 'late' && <Clock className="w-4 h-4 mr-1" />}
                  <span className="capitalize">{selectedSession.attendance.status || 'N/A'}</span>
                </span>
              </div>
            )}

            {/* Meeting Link for Virtual Sessions */}
            {selectedSession.meetingLink && (selectedSession.type === 'virtual' || selectedSession.type === 'online') && (
              <div className="p-4 rounded-xl border border-blue-200 bg-blue-50">
                <p className="text-xs uppercase tracking-wide text-blue-700 mb-2">Meeting Link</p>
                <a
                  href={selectedSession.meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 underline break-all"
                >
                  {selectedSession.meetingLink}
                </a>
              </div>
            )}

            {/* Description/Agenda */}
            {(selectedSession.description || selectedSession.agenda) && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Description</h4>
                <p className="text-sm text-gray-700 whitespace-pre-line break-words">
                  {selectedSession.description || selectedSession.agenda || 'No description available.'}
                </p>
              </div>
            )}

            {/* Trainers */}
            {selectedSession.instructor && selectedSession.instructor.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Trainer{selectedSession.instructor.length > 1 ? 's' : ''}</h4>
                <div className="space-y-2">
                  {selectedSession.instructor.map((instructor, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 break-words">
                            {instructor.name || 'Trainer'}
                          </p>
                          {instructor.email && (
                            <p className="text-xs text-gray-500 mt-1 break-all">
                              {instructor.email}
                            </p>
                          )}
                          {instructor.phone && (
                            <p className="text-xs text-gray-500 mt-1">
                              {instructor.phone}
                            </p>
                          )}
                          {instructor.expertise && instructor.expertise.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {instructor.expertise.map((exp, expIdx) => (
                                <span
                                  key={expIdx}
                                  className="px-2 py-0.5 text-xs rounded-full bg-purple-50 text-purple-700 border border-purple-100"
                                >
                                  {exp}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Materials */}
            {selectedSession.materials && selectedSession.materials.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Materials</h4>
                <div className="space-y-2">
                  {selectedSession.materials.map((material, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 break-words">
                          {material.name || material.filename || 'Material'}
                        </p>
                        {material.type && (
                          <p className="text-xs text-gray-500 mt-1">
                            Type: {formatDisplayValue(material.type)}
                          </p>
                        )}
                        {material.size && (
                          <p className="text-xs text-gray-500 mt-1">
                            Size: {(material.size / 1024).toFixed(2)} KB
                          </p>
                        )}
                      </div>
                      {material.url && (
                        <a
                          href={material.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-4 px-3 py-1 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                        >
                          <Download className="w-4 h-4 inline mr-1" />
                          Download
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Objectives */}
            {selectedSession.objectives && selectedSession.objectives.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Learning Objectives</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedSession.objectives.map((objective, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full text-xs bg-indigo-50 text-indigo-700 border border-indigo-100"
                    >
                      {objective}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Prerequisites */}
            {selectedSession.prerequisites && selectedSession.prerequisites.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Prerequisites</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedSession.prerequisites.map((prereq, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full text-xs bg-yellow-50 text-yellow-700 border border-yellow-100"
                    >
                      {prereq}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {selectedSession.notes && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Additional Notes</h4>
                <p className="text-sm text-gray-700 whitespace-pre-line break-words">
                  {selectedSession.notes}
                </p>
              </div>
            )}

            {/* Actual Times (if different from scheduled) */}
            {(selectedSession.actualStartTime || selectedSession.actualEndTime) && (
              <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Actual Times</p>
                <div className="space-y-1">
                  {selectedSession.actualStartTime && (
                    <p className="text-sm text-gray-700">
                      Started: {formatDate(selectedSession.actualStartTime, { includeTime: true })}
                    </p>
                  )}
                  {selectedSession.actualEndTime && (
                    <p className="text-sm text-gray-700">
                      Ended: {formatDate(selectedSession.actualEndTime, { includeTime: true })}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end bg-gray-50">
            <button
              onClick={() => {
                setShowSessionModal(false);
                setSelectedSession(null);
              }}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'courses', label: 'Courses', icon: BookOpen },
    { id: 'quizzes', label: 'Quizzes', icon: Brain },
    { id: 'learningPaths', label: 'Learning Paths', icon: Share2 },
    { id: 'sessions', label: 'Sessions', icon: Users },
    { id: 'certificates', label: 'Certificates', icon: Award }
  ];

  const renderTabLoader = (message) => (
    <div className="text-center py-12">
      <RefreshCw className="mx-auto h-12 w-12 text-gray-400 animate-spin" />
      <h3 className="mt-2 text-sm font-medium text-gray-900">{message}</h3>
    </div>
  );

  const renderActiveTabContent = () => {
    switch (activeTab) {
      case 'courses':
        if (coursesLoading) {
          return renderTabLoader('Loading your courses...');
        }
        return renderCourses();
      case 'quizzes':
        if (quizzesLoading) {
          return renderTabLoader('Loading your quizzes...');
        }
        return renderQuizzes();
      case 'learningPaths':
        if (learningPathsLoading) {
          return renderTabLoader('Loading learning paths...');
        }
        return renderLearningPaths();
      case 'sessions':
        if (sessionsLoading) {
          return renderTabLoader('Loading training sessions...');
        }
        return renderSessions();
      case 'certificates':
        if (certificatesLoading) {
          return renderTabLoader('Loading certificates...');
        }
        return renderCertificates();
      default:
        return renderCourses();
    }
  };

  const renderSessions = () => {
    // Sessions are already sorted and paginated by the backend

    // Use API pagination data
    const totalPages = sessionsTotalPages;
    const totalSessions = sessionsTotal;

    // Pagination handlers
    const handlePageChange = (page) => {
      if (page < 1 || page > totalPages || page === sessionsCurrentPage) return;
      setSessionsCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (!sessionsLoading && sessions.length === 0) {
      return (
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No upcoming sessions</h3>
          <p className="mt-1 text-sm text-gray-500">You don't have any scheduled training sessions at the moment.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Sessions Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Session Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mode</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attendance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(sessionsLoading ? [] : sessions).map((session) => {
                  const sessionDate = session.startTime ? new Date(session.startTime) : null;
                  const sessionDateStr = sessionDate ? sessionDate.toISOString().split('T')[0] : null;

                  return (
                    <tr key={session.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs" title={session.title}>
                          {truncateText(session.title || 'Untitled Session', 30)}
                        </div>
                        {session.location && (
                          <div className="mt-1 text-xs text-gray-500 flex items-center">
                            <MapPin className="w-3 h-3 mr-1" />
                            {truncateText(session.location, 25)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 truncate max-w-xs" title={session.courseTitle || 'N/A'}>
                          {truncateText(session.courseTitle || 'N/A', 30)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {sessionDateStr ? (
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                              {formatDate(sessionDateStr, { includeTime: false })}
                            </div>
                          ) : (
                            'N/A'
                          )}
                          {session.startTime && session.endTime && (
                            <div className="flex items-center mt-1">
                              <Clock className="h-4 w-4 mr-1 text-gray-400" />
                              <span className="text-xs text-gray-600">
                                {formatTimeOnly(session.startTime)} - {formatTimeOnly(session.endTime)}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {session.type === 'classroom' || session.type === 'in-person' ? (
                            <>
                              <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                In-Person
                              </span>
                            </>
                          ) : (
                            <>
                              <Video className="h-4 w-4 mr-1 text-gray-400" />
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                                Virtual
                              </span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${session.role === 'trainer' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                          {session.role === 'trainer' ? 'Trainer' : 'Participant'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {session.attendance ? (
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full flex items-center ${session.attendance.status === 'present' ? 'bg-green-100 text-green-800' :
                            session.attendance.status === 'absent' ? 'bg-red-100 text-red-800' :
                              session.attendance.status === 'late' ? 'bg-yellow-100 text-yellow-800' :
                                session.attendance.status === 'excused' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                            }`}>
                            {session.attendance.status === 'present' && <CheckCircle className="w-3 h-3 mr-1" />}
                            {session.attendance.status === 'absent' && <XCircle className="w-3 h-3 mr-1" />}
                            {session.attendance.status === 'late' && <Clock className="w-3 h-3 mr-1" />}
                            {session.attendance.status || 'N/A'}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Not marked</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => {
                            setSelectedSession(session);
                            setShowSessionModal(true);
                          }}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {renderTabPagination(
          sessionsCurrentPage,
          totalSessions,
          sessionsItemsPerPage,
          handlePageChange
        )}
      </div>
    );
  };

  const renderLearningPaths = () => {
    if (learningPathsLoading) {
      return (
        <div className="space-y-4">
          <div className="text-center py-12">
            <RefreshCw className="mx-auto h-10 w-10 text-gray-400 animate-spin" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Loading learning paths...</h3>
          </div>
        </div>
      );
    }

    if (learningPathsError) {
      return (
        <div className="space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Unable to load learning paths</h3>
                <p className="text-sm text-red-700 mt-1">{learningPathsError}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (learningPaths.length === 0) {
      return (
        <div className="text-center py-12">
          <Share2 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No learning paths yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Published learning paths assigned to you will appear here.
          </p>
        </div>
      );
    }

    const totalPaths = learningPaths.length;
    const totalPages = Math.max(Math.ceil(totalPaths / learningPathsItemsPerPage), 1);
    const startIndex = (learningPathsCurrentPage - 1) * learningPathsItemsPerPage;
    const endIndex = Math.min(startIndex + learningPathsItemsPerPage, totalPaths);
    const paginatedPaths = learningPaths.slice(startIndex, endIndex);

    const handlePageChange = (page) => {
      if (page < 1 || page > totalPages || page === learningPathsCurrentPage) return;
      setLearningPathsCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
      <div className="space-y-4">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Courses</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {paginatedPaths.map((path) => (
                  <tr key={path.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate max-w-xs capitalize break-words" title={path.title}>{path.title}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{formatDisplayValue(path.category)}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{path.totalCourses || 0}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{formatDuration(path.totalDurationMinutes || 0)}</td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => {
                          sessionStorage.setItem('trainingNavContext', 'my_training');
                          navigate(`/hrms/training/learning-path/${path.id}`);
                        }}
                        className="inline-flex items-center px-3 py-1.5 border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50 text-sm"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {renderTabPagination(learningPathsCurrentPage, totalPaths, learningPathsItemsPerPage, handlePageChange)}
      </div>
    );
  };

  // Filtering is now done on the backend via API

  useEffect(() => {
    setQuizzesCurrentPage(1);
  }, [quizStatusFilter, debouncedSearchTerm]);

  useEffect(() => {
    if (quizzesCurrentPage > quizzesTotalPages && quizzesTotalPages > 0) {
      setQuizzesCurrentPage(quizzesTotalPages);
    }
  }, [quizzesTotalPages, quizzesCurrentPage]);

  const renderQuizzes = () => {
    // Use API pagination data - filtering is done on backend
    const totalPages = quizzesTotalPages;
    const totalQuizzes = quizzesTotal;

    const handlePageChange = (page) => {
      if (page < 1 || page > totalPages || page === quizzesCurrentPage) return;
      setQuizzesCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
      <div className="space-y-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-end gap-4 w-full">
            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={quizStatusFilter}
                onChange={(e) => setQuizStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white h-[42px]"
              >
                <option value="all">All Status</option>
                <option value="assigned">Assigned</option>
                <option value="passed">Passed</option>
                <option value="failed">Failed</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
            {quizStatusFilter !== 'all' && (
              <div className="flex-shrink-0">
                <button
                  onClick={() => setQuizStatusFilter('all')}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium h-[42px]"
                >
                  <X className="h-4 w-4" />
                  <span>Clear</span>
                </button>
              </div>
            )}
          </div>
        </div>
        {!quizzesLoading && quizzes.length === 0 ? (
          <div className="text-center py-12">
            <Brain className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No quizzes assigned</h3>
            <p className="mt-1 text-sm text-gray-500">
              {quizStatusFilter === 'all'
                ? 'Quizzes assigned to you will appear here.'
                : 'No quizzes match the selected status.'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pass Score</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {(quizzesLoading ? [] : quizzes).map((quiz) => {
                    const statusMeta = getQuizStatusMeta(quiz);
                    const quizDisplayStatus = getQuizDisplayStatus(quiz);
                    return (
                      <tr key={quiz.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <p className="text-sm font-semibold text-gray-900 break-words capitalize">{quiz.title}</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {quiz.lastAttempt && quiz.lastAttempt.score != null ? `${quiz.lastAttempt.score}%` : 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {quiz.passScore != null ? `${quiz.passScore}%` : 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {quiz.dueDate ? formatDate(quiz.dueDate, { includeTime: false }) : 'No due date'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusMeta.color}`}>
                            {statusMeta.icon}
                            <span className="ml-1 capitalize">{statusMeta.label}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => openQuizPlayer(quiz)}
                            className="inline-flex items-center px-3 py-1.5 border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50 text-sm"
                          >
                            <Play className="w-4 h-4 mr-1" />
                            {quizDisplayStatus === 'assigned' ? 'Start Quiz' : 'Result'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {renderTabPagination(quizzesCurrentPage, totalQuizzes, quizzesItemsPerPage, handlePageChange)}
      </div>
    );
  };

  const renderQuizPlayerModal = () => {
    if (!quizPlayerState.open) {
      return null;
    }

    const quiz = quizPlayerState.quiz;
    const canSubmit = quiz && !quizPlayerState.result;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
        <div className="bg-white rounded-lg p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1 min-w-0 pr-4">
              <h3 className="text-xl font-semibold text-gray-900 capitalize break-words">{quiz?.title || 'Quiz'}</h3>
              {quiz && (
                <p className="text-sm text-gray-500">
                  Pass Score: {quiz.passScore ?? 70}%
                </p>
              )}
            </div>
            <button
              onClick={closeQuizPlayer}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>

          {quizPlayerState.loading && (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          )}

          {!quizPlayerState.loading && (
            <>
              {quizPlayerState.error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-sm text-red-600 rounded-lg">
                  {quizPlayerState.error}
                </div>
              )}

              {quiz && (
                <>
                  {quiz.instructions && (
                    <div className="mb-4 p-4 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-700 break-words break-all">
                      {quiz.instructions}
                    </div>
                  )}

                  {quizPlayerState.result ? (
                    <div className="space-y-4">
                      <div className={`p-4 rounded-lg border ${quizPlayerState.result.passed ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
                        <p className="text-lg font-semibold">
                          {quizPlayerState.result.passed ? 'Congratulations! You passed the quiz.' : 'Quiz completed'}
                        </p>
                        <p className="text-sm mt-1">
                          Score: {quizPlayerState.result.score}% ({quizPlayerState.result.correctAnswers}/{quizPlayerState.result.totalQuestions} correct)
                        </p>
                        <p className="text-sm">
                          Submitted on {quizPlayerState.result.submittedAt ? formatDate(new Date(quizPlayerState.result.submittedAt), { includeTime: true }) : formatDate(new Date(), { includeTime: true })}
                        </p>
                      </div>

                      {quizPlayerState.result.answersReview && (
                        <div className="space-y-3">
                          {quizPlayerState.result.answersReview.map((answer, index) => (
                            <div key={answer.questionId || index} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2 gap-2">
                                <p className="text-sm font-semibold text-gray-900 capitalize break-words break-all flex-1">
                                  {index + 1}. {answer.questionText}
                                </p>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${answer.isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                  {answer.isCorrect ? 'Correct' : 'Incorrect'}
                                </span>
                              </div>
                              <div className="text-sm space-y-1">
                                <p className="text-gray-700 break-words break-all">
                                  <span className="font-medium">Your answer: </span>
                                  {answer.selectedText || 'Not answered'}
                                </p>
                                {!answer.isCorrect && (
                                  <p className="text-gray-700 break-words break-all">
                                    <span className="font-medium">Correct answer: </span>
                                    {answer.correctText || 'Not available'}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-end">
                        <button
                          onClick={closeQuizPlayer}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-4">
                        {(quiz.questions || []).map((question, index) => (
                          <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                            <p className="text-sm font-semibold text-gray-900 mb-3 capitalize">
                              {index + 1}. {question.question || question.title}
                            </p>
                            {question.options && question.options.length > 0 ? (
                              <div className="space-y-2">
                                {question.options.map((option) => (
                                  <label
                                    key={option.id}
                                    className={`flex items-center space-x-2 text-sm cursor-pointer px-3 py-2 rounded-lg border ${quizPlayerState.answers[question.id] === option.id
                                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                                      : 'border-gray-200 hover:border-blue-200'
                                      }`}
                                  >
                                    <input
                                      type="radio"
                                      name={`question-${question.id}`}
                                      value={option.id}
                                      checked={quizPlayerState.answers[question.id] === option.id}
                                      onChange={() => handleQuizAnswerChange(question.id, option.id)}
                                      disabled={!canSubmit}
                                    />
                                    <span>{option.text || 'Option'}</span>
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <textarea
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                rows={3}
                                placeholder="Type your answer..."
                                value={quizPlayerState.answers[question.id] || ''}
                                onChange={(e) => handleQuizAnswerChange(question.id, e.target.value)}
                                disabled={!canSubmit}
                              />
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="flex items-center justify-end mt-6 space-x-3">
                        <button
                          onClick={closeQuizPlayer}
                          className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSubmitQuizAttempt}
                          disabled={!canSubmit || quizPlayerState.submitting}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                        >
                          {quizPlayerState.submitting ? 'Submitting...' : 'Submit Quiz'}
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Training</h1>
            {summary && (
              <p className="text-sm text-gray-500 mt-1">
                {summary.totalCourses} courses • {summary.totalQuizzes || 0} quizzes • {summary.completed} done • {summary.upcomingSessions} sessions
              </p>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Error loading training data</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-blue-300'
                      }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        {renderActiveTabContent()}

        {/* Modals */}
        {showCoursePlayer && renderCoursePlayer()}
        {showQuizModal && renderQuizModal()}
        {showCertificateModal && renderCertificateModal()}
        {showSessionModal && renderSessionModal()}
        {quizPlayerState.open && renderQuizPlayerModal()}
        {showDetailModal && renderCourseDetailModal()}
        {showLearningPathModal && selectedLearningPath && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-8 py-6 border-b border-gray-200">
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-blue-500 uppercase tracking-[0.2em]">Learning Path</p>
                  <h3 className="text-2xl font-bold text-gray-900 break-words capitalize">{selectedLearningPath.title}</h3>
                  <div className="flex flex-wrap gap-2 mt-2 text-xs">
                    <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                      {formatDisplayValue(selectedLearningPath.category)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowLearningPathModal(false);
                    setSelectedLearningPath(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                  <p className="text-sm text-gray-700 leading-relaxed break-words">
                    {formatPathDescription(selectedLearningPath.description)}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    {
                      label: 'Courses',
                      value: selectedLearningPath.totalCourses || 0
                    },
                    {
                      label: 'Total Duration',
                      value: formatDuration(selectedLearningPath.totalDurationMinutes || 0)
                    },
                    {
                      label: 'Category',
                      value: formatDisplayValue(selectedLearningPath.category)
                    }
                  ].map((stat) => (
                    <div key={stat.label} className="p-4 border border-gray-100 rounded-xl bg-white shadow-sm">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">{stat.label}</p>
                      <p className="text-lg font-semibold text-gray-900 mt-1">{stat.value}</p>
                    </div>
                  ))}
                </div>

                {selectedLearningPath.courses && selectedLearningPath.courses.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                        Courses in this path
                      </p>
                      <span className="text-xs text-gray-500">
                        Ordered completion • {selectedLearningPath.courses.length} course
                        {selectedLearningPath.courses.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="border border-gray-100 rounded-xl overflow-hidden">
                      <div className="grid grid-cols-12 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 px-4 py-2">
                        <div className="col-span-6">Course</div>
                        <div className="col-span-3">Course Type</div>
                        <div className="col-span-2">Level</div>
                        <div className="col-span-1 text-right">Duration</div>
                      </div>
                      {selectedLearningPath.courses.map((course) => (
                        <div
                          key={`${selectedLearningPath.id}-${course.id || course.order}`}
                          className="grid grid-cols-12 items-center px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors text-sm"
                        >
                          <div className="col-span-6 min-w-0">
                            <p className="font-medium text-gray-900 break-words capitalize">
                              {course.order ? `${course.order}. ` : ''}
                              {course.title}
                            </p>
                          </div>
                          <div className="col-span-3 text-gray-600 truncate">
                            {formatDisplayValue(course.category)}
                          </div>
                          <div className="col-span-2 text-gray-600 truncate">
                            {formatDisplayValue(course.level)}
                          </div>
                          <div className="col-span-1 text-right text-gray-500">
                            {formatDuration(course.duration || 0)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedLearningPath.learningObjectives && selectedLearningPath.learningObjectives.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                      Learning Objectives
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {selectedLearningPath.learningObjectives.map((objective, index) => (
                        <span
                          key={`${selectedLearningPath.id}-objective-${index}`}
                          className="px-3 py-1 rounded-full text-xs bg-indigo-50 text-indigo-700 border border-indigo-100"
                        >
                          {objective}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="px-8 py-4 border-t border-gray-200 flex justify-end bg-gray-50">
                <button
                  onClick={() => {
                    setShowLearningPathModal(false);
                    setSelectedLearningPath(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

};

export default MyTraining;
